import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import styles from './AdminShared.module.scss';
import SearchDropdown from './components/SearchDropdown';
import {
    AdminEventCreateDto,
    AdminEventDto,
    AdminLocationCreateDto,
    AdminLocationDto,
    AdminVideoDetailDto,
    AdminVideoMediaFileDto,
    AdminVideoMediaHealthDto,
    AdminVideoParticipantAssignmentDto,
    AdminVideoUpsertDto,
    ParticipantDto,
    ParticipantCreateDto,
    TagDto,
    VideoParticipantTypeDto
} from '../../../types/admin.types';
import {
    completeAdminVideoMediaReplacementUpload,
    createAdminEvent,
    createAdminLocation,
    createAdminVideoDraft,
    createAdminVideoMediaReplacement,
    createAdminVideoMediaReplacementUploadUrl,
    createParticipant,
    createAdminTag,
    fetchAdminEvents,
    fetchAdminLocations,
    fetchAdminTags,
    fetchAdminVideo,
    fetchAdminVideoMediaHealth,
    fetchSiteConfiguration,
    fetchParticipants,
    fetchVideoParticipantTypes,
    repairAdminVideoMedia,
    retryAdminVideoMediaReplacementPrimary,
    abandonAdminVideoMediaReplacement,
    upsertAdminVideo,
    updateAdminVideoPlaybackConfiguration,
    uploadToPresignedUrl
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { Routes } from '../../../types/constants';
import { isSuperAdmin } from '../../../utils/roles';
import AdminDateTimeInput from './components/AdminDateTimeInput';
import ParticipantFormFields, { normalizeParticipantCreatePayload } from './components/ParticipantFormFields';
import EventFormFields, {
    emptyEventForm,
    normalizeEventCreatePayload
} from './components/EventFormFields';
import VenueFormFields, {
    emptyVenueForm,
    normalizeVenueCreatePayload
} from './components/VenueFormFields';
import {
    toDateInput,
    toDateTimeLocalInput,
    toUtcDateString,
    toUtcIsoString
} from '../../../utils/date-time';
import { copyTextToClipboard } from '../../../utils/share.utils';
import { useAdminUnsavedChanges } from './useAdminUnsavedChanges';
import AdminVideoPlayerTools from './AdminVideoPlayerTools';
import { generateAdminFightTitle } from './admin-video-title';

const statusLabels: Record<number, string> = {
    0: 'Queued',
    1: 'Processing',
    2: 'Complete',
    3: 'Failed',
    4: 'Preparing',
    5: 'Removed'
};

const generationLifecycleLabels: Record<number, string> = {
    0: 'Candidate',
    1: 'Active',
    2: 'Superseded',
    3: 'Abandoned'
};

const cornerColorOptions: Array<{ value: 'blue' | 'red'; label: string }> = [
    { value: 'blue', label: 'Blue' },
    { value: 'red', label: 'Red' }
];

const defaultUpsertForm: AdminVideoUpsertDto = {
    Title: '',
    Description: '',
    PublishStatus: 0,
    EventType: 'General',
    EventId: null,
    LocationId: null,
    EventOrder: null,
    VideoDateOverride: null,
    PurchasePrice: null,
    PurchaseAccessUntil: null,
    PurchaseEnabled: true,
    ThumbnailCaptureSeconds: null,
    Tags: [],
    Participants: []
};

const emptyParticipantForm: ParticipantCreateDto = {
    Name: '',
    Nickname: '',
    ProfileImageBase64: '',
    DateOfBirth: ''
};

type UploadState = {
    videoId: string;
    mediaGenerationId?: string;
    uploadAttemptId?: string;
    presignedUrl: string;
    file: File;
};

type CreateVenueTarget = 'video-form' | 'event-modal';
type VideoEditorTab = 'details' | 'participants' | 'publishing' | 'media' | 'super-admin';

const videoFormat = {
    originalSource: 0,
    webmPlayback: 1,
    primaryPlayback: 2,
    mobilePlayback: 3
} as const;

const normalizeName = (value?: string | null) => (value ?? '').trim().toLowerCase();

const isMp4VideoFile = (selectedFile: File) => {
    const contentType = selectedFile.type.trim().toLowerCase();
    return contentType
        ? contentType === 'video/mp4'
        : selectedFile.name.trim().toLowerCase().endsWith('.mp4');
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return '-';
    }

    return parsed.toLocaleString();
};

const formatDuration = (value?: string | null) => {
    const duration = value?.trim();
    if (!duration) return 'Unknown';

    const numericParts = duration.match(/\d+(?:\.\d+)?/g);
    return numericParts?.every((part) => Number(part) === 0) ? 'Unknown' : duration;
};

const formatBytes = (value?: number | null) => {
    if (value == null) {
        return '-';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${size.toFixed(unitIndex === 0 || size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const normalizeCornerColor = (value?: string | null): 'blue' | 'red' | null => {
    const normalized = (value ?? '').trim().toLowerCase();
    return normalized === 'blue' || normalized === 'red' ? normalized : null;
};

const mapLocation = (item: any): AdminLocationDto => ({
    id: item.id ?? item.Id ?? '',
    name: item.name ?? item.Name ?? '',
    city: item.city ?? item.City ?? null,
    region: item.region ?? item.Region ?? null,
    country: item.country ?? item.Country ?? null,
    venueUrl: item.venueUrl ?? item.VenueUrl ?? null,
    isArchived: item.isArchived ?? item.IsArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? ''
});

const mapEvent = (item: any, fallback?: Partial<AdminEventDto>): AdminEventDto => ({
    id: item.id ?? item.Id ?? fallback?.id ?? '',
    name: item.name ?? item.Name ?? fallback?.name ?? '',
    description: item.description ?? item.Description ?? fallback?.description ?? null,
    eventDate: item.eventDate ?? item.EventDate ?? fallback?.eventDate ?? '',
    eventUrl: item.eventUrl ?? item.EventUrl ?? fallback?.eventUrl ?? null,
    thumbnailUrl: item.thumbnailUrl ?? item.ThumbnailUrl ?? fallback?.thumbnailUrl ?? null,
    locationId: item.locationId ?? item.LocationId ?? fallback?.locationId ?? null,
    locationName: item.locationName ?? item.LocationName ?? fallback?.locationName ?? null,
    tags: item.tags ?? item.Tags ?? fallback?.tags ?? [],
    hideFromFightCards: item.hideFromFightCards ?? item.HideFromFightCards ?? fallback?.hideFromFightCards ?? false,
    isArchived: item.isArchived ?? item.IsArchived ?? fallback?.isArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? fallback?.createdDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? fallback?.updatedDate ?? ''
});

const mapTag = (item: any): TagDto => ({
    id: item.id ?? item.Id ?? '',
    name: item.name ?? item.Name ?? '',
    usageCount: item.usageCount ?? item.UsageCount ?? 0,
    lastUsedDate: item.lastUsedDate ?? item.LastUsedDate ?? null
});

const normalizeUpsertPayload = (value: AdminVideoUpsertDto): AdminVideoUpsertDto => ({
    ...value,
    EventType: value.EventType.trim() || 'General',
    EventOrder: value.EventOrder ?? null,
    VideoDateOverride: toUtcIsoString(value.VideoDateOverride),
    PurchaseAccessUntil: toUtcDateString(value.PurchaseAccessUntil ?? null),
    PurchaseEnabled: value.PurchaseEnabled ?? true
});

const validateVideoForm = (value: AdminVideoUpsertDto): { tab: VideoEditorTab; fieldId: string; message: string } | null => {
    if (!value.Title.trim()) {
        return { tab: 'details', fieldId: 'video-title', message: 'Title is required.' };
    }
    if (value.EventOrder != null && value.EventOrder < 1) {
        return { tab: 'details', fieldId: 'video-event-order', message: 'Fight number must be 1 or greater.' };
    }
    if (
        value.ThumbnailCaptureSeconds != null
        && (!Number.isInteger(value.ThumbnailCaptureSeconds) || value.ThumbnailCaptureSeconds < 0)
    ) {
        return { tab: 'media', fieldId: 'video-thumbnail-seconds', message: 'Thumbnail capture must be a whole number of seconds, 0 or greater.' };
    }

    const incompleteParticipantIndex = value.Participants.findIndex((participant) => (
        !participant.ParticipantId.trim() || !participant.TypeId.trim()
    ));
    if (incompleteParticipantIndex >= 0) {
        const participant = value.Participants[incompleteParticipantIndex];
        const missingFields = [
            !participant.ParticipantId.trim() ? 'participant' : null,
            !participant.TypeId.trim() ? 'role' : null
        ].filter(Boolean).join(' and ');
        return {
            tab: 'participants',
            fieldId: !participant.ParticipantId.trim()
                ? `video-participant-${incompleteParticipantIndex}`
                : `video-participant-role-${incompleteParticipantIndex}`,
            message: `Participant row ${incompleteParticipantIndex + 1} needs a ${missingFields}. Complete or remove this row before saving.`
        };
    }

    return null;
};

const AdminVideos = () => {
    const { videoId: routeVideoId } = useParams<{ videoId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { getUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const token = userInfo?.token;
    const isSuperAdminUser = isSuperAdmin(userInfo?.roles);
    const isNewRoute = location.pathname === Routes.ADMIN_VIDEO_NEW;

    const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
    const [detail, setDetail] = useState<AdminVideoDetailDto | null>(null);
    const [mediaHealth, setMediaHealth] = useState<AdminVideoMediaHealthDto | null>(null);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [mediaErrorMessage, setMediaErrorMessage] = useState<string | null>(null);
    const [repairingFormat, setRepairingFormat] = useState<number | null>(null);
    const [replacementFile, setReplacementFile] = useState<File | null>(null);
    const [mediaAction, setMediaAction] = useState<string | null>(null);
    const [form, setForm] = useState<AdminVideoUpsertDto>(defaultUpsertForm);
    const [mobilePlaybackOverride, setMobilePlaybackOverride] = useState<boolean | null>(null);
    const [presignedDeliveryOverride, setPresignedDeliveryOverride] = useState<boolean | null>(null);
    const [apiStreamFallbackEnabled, setApiStreamFallbackEnabled] = useState(true);
    const [file, setFile] = useState<File | null>(null);
    const [uploadState, setUploadState] = useState<UploadState | null>(null);
    const [tagInput, setTagInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [metadataSaveRetryRequired, setMetadataSaveRetryRequired] = useState(false);
    const [activeTab, setActiveTab] = useState<VideoEditorTab>('details');
    const [allowNavigationAfterSave, setAllowNavigationAfterSave] = useState(false);

    const [events, setEvents] = useState<AdminEventDto[]>([]);
    const [locations, setLocations] = useState<AdminLocationDto[]>([]);
    const [participants, setParticipants] = useState<ParticipantDto[]>([]);
    const [participantTypes, setParticipantTypes] = useState<VideoParticipantTypeDto[]>([]);
    const [tags, setTags] = useState<TagDto[]>([]);
    const [participantSearchByIndex, setParticipantSearchByIndex] = useState<Record<number, string>>({});
    const [eventSearchInput, setEventSearchInput] = useState('');
    const [locationSearchInput, setLocationSearchInput] = useState('');
    const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
    const [createEventForm, setCreateEventForm] = useState<AdminEventCreateDto>(emptyEventForm);
    const [createEventLocationSearchInput, setCreateEventLocationSearchInput] = useState('');
    const [isCreatingEvent, setIsCreatingEvent] = useState(false);
    const [createEventError, setCreateEventError] = useState<string | null>(null);
    const [isCreateParticipantModalOpen, setIsCreateParticipantModalOpen] = useState(false);
    const [createParticipantAssignmentIndex, setCreateParticipantAssignmentIndex] = useState<number | null>(null);
    const [createParticipantForm, setCreateParticipantForm] = useState<ParticipantCreateDto>(emptyParticipantForm);
    const [isCreatingParticipant, setIsCreatingParticipant] = useState(false);
    const [createParticipantError, setCreateParticipantError] = useState<string | null>(null);
    const [isCreateVenueModalOpen, setIsCreateVenueModalOpen] = useState(false);
    const [createVenueTarget, setCreateVenueTarget] = useState<CreateVenueTarget>('video-form');
    const [createVenueForm, setCreateVenueForm] = useState<AdminLocationCreateDto>(emptyVenueForm);
    const [isCreatingVenue, setIsCreatingVenue] = useState(false);
    const [createVenueError, setCreateVenueError] = useState<string | null>(null);
    const detailRequestSequenceRef = useRef(0);
    const mediaRequestSequenceRef = useRef(0);
    const repairRequestSequenceRef = useRef(0);
    const replacementRequestSequenceRef = useRef(0);
    const eventLookupRequestSequenceRef = useRef(0);
    const locationLookupRequestSequenceRef = useRef(0);
    const participantLookupRequestSequenceRef = useRef(0);
    const tagLookupRequestSequenceRef = useRef(0);
    const editorOperationEpochRef = useRef(0);
    const uploadOperationSequenceRef = useRef(0);
    const activeUploadOperationRef = useRef<number | null>(null);
    const draftOperationSequenceRef = useRef(0);
    const activeDraftOperationRef = useRef<number | null>(null);
    const participantModalOperationRef = useRef(0);
    const eventModalOperationRef = useRef(0);
    const venueModalOperationRef = useRef(0);
    const tagCreateOperationRef = useRef(0);
    const activeVideoIdRef = useRef<string | null>(null);
    const pendingSavedDraftNavigationRef = useRef<{ videoId: string; editorEpoch: number } | null>(null);
    const formRef = useRef(form);
    const savedPlaybackOverridesRef = useRef<{
        mobilePlaybackOverride: boolean | null;
        presignedDeliveryOverride: boolean | null;
    }>({
        mobilePlaybackOverride: null,
        presignedDeliveryOverride: null
    });

    const selectedEvent = useMemo(
        () => events.find((item) => item.id === form.EventId),
        [events, form.EventId]
    );

    formRef.current = form;

    const activeLocations = useMemo(
        () => locations.filter((location) => !location.isArchived),
        [locations]
    );

    const activeEventOptions = useMemo(
        () => events
            .filter((item) => !item.isArchived)
            .map((item) => ({
                value: item.id,
                label: item.name
            })),
        [events]
    );

    const activeLocationOptions = useMemo(
        () => activeLocations.map((item) => ({
            value: item.id,
            label: item.name,
            subLabel: [item.city, item.region, item.country].filter(Boolean).join(', ')
        })),
        [activeLocations]
    );

    const participantOptions = useMemo(
        () => participants.map((participant) => ({
            value: participant.Id,
            label: participant.Nickname
                ? `${participant.Name} (${participant.Nickname})`
                : participant.Name
        })),
        [participants]
    );

    const participantTypeOptions = useMemo(
        () => participantTypes.map((participantType) => ({
            value: participantType.Id,
            label: participantType.Name
        })),
        [participantTypes]
    );

    const effectiveEventDate = useMemo(() => {
        if (form.VideoDateOverride) {
            return toUtcIsoString(form.VideoDateOverride);
        }

        if (form.EventId) {
            return selectedEvent?.eventDate ?? detail?.EffectiveEventDate ?? null;
        }

        return null;
    }, [detail?.EffectiveEventDate, form.EventId, form.VideoDateOverride, selectedEvent?.eventDate]);

    const mapDetailToForm = useCallback((response: AdminVideoDetailDto): AdminVideoUpsertDto => ({
        Title: response.Title,
        Description: response.Description ?? '',
        PublishStatus: response.PublishStatus,
        EventType: response.EventType ?? '',
        EventId: response.EventId ?? null,
        LocationId: response.EventId ? null : response.LocationId ?? null,
        EventOrder: response.EventOrder ?? null,
        VideoDateOverride: toDateTimeLocalInput(response.VideoDateOverride),
        PurchasePrice: response.PurchasePrice ?? null,
        PurchaseAccessUntil: toDateInput(response.PurchaseAccessUntil),
        PurchaseEnabled: response.PurchaseEnabled ?? true,
        ThumbnailCaptureSeconds: response.ThumbnailCaptureSeconds ?? null,
        Tags: response.Tags ?? [],
        Participants: (response.Participants ?? []).map((item) => ({
            ParticipantId: item.ParticipantId,
            TypeId: item.TypeId,
            CornerColor: normalizeCornerColor(item.CornerColor ?? null)
        }))
    }), []);

    const loadLookups = useCallback(async () => {
        const eventRequestSequence = ++eventLookupRequestSequenceRef.current;
        const locationRequestSequence = ++locationLookupRequestSequenceRef.current;
        const participantRequestSequence = ++participantLookupRequestSequenceRef.current;
        const tagRequestSequence = ++tagLookupRequestSequenceRef.current;
        try {
            const [eventsResponse, locationsResponse, participantsResponse, participantTypesResponse, tagsResponse] = await Promise.all([
                fetchAdminEvents(true, token),
                fetchAdminLocations(true, token),
                fetchParticipants(token),
                fetchVideoParticipantTypes(token),
                fetchAdminTags(token)
            ]);

            if (eventLookupRequestSequenceRef.current === eventRequestSequence) {
                setEvents(eventsResponse.map((item: any) => mapEvent(item)));
            }
            if (locationLookupRequestSequenceRef.current === locationRequestSequence) {
                setLocations((locationsResponse ?? []).map((item: any) => mapLocation(item)));
            }
            if (participantLookupRequestSequenceRef.current === participantRequestSequence) {
                setParticipants(Array.isArray(participantsResponse) ? participantsResponse : []);
            }
            setParticipantTypes(participantTypesResponse);
            if (tagLookupRequestSequenceRef.current === tagRequestSequence) {
                setTags((tagsResponse ?? []).map((item: any) => mapTag(item)));
            }
        } catch (error) {
            setErrorMessage('Unable to load video metadata.');
        }
    }, [token]);

    useEffect(() => {
        loadLookups();
    }, [loadLookups]);

    useEffect(() => {
        if (!isSuperAdminUser) {
            setApiStreamFallbackEnabled(true);
            return;
        }

        let isActive = true;
        fetchSiteConfiguration(token)
            .then((configuration) => {
                if (isActive) {
                    setApiStreamFallbackEnabled(configuration.EnableApiStreamFallback ?? true);
                }
            })
            .catch(() => {
                if (isActive) {
                    setApiStreamFallbackEnabled(true);
                }
            });

        return () => {
            isActive = false;
        };
    }, [isSuperAdminUser, token]);

    const loadMediaHealth = useCallback(async (videoId: string, retryOnce = false) => {
        if (activeVideoIdRef.current !== videoId) {
            return;
        }

        const requestSequence = ++mediaRequestSequenceRef.current;
        setMediaLoading(true);
        setMediaErrorMessage(null);
        try {
            let response: AdminVideoMediaHealthDto | null = null;
            let lastError: unknown;
            const attempts = retryOnce ? 2 : 1;
            for (let attempt = 0; attempt < attempts; attempt += 1) {
                try {
                    response = await fetchAdminVideoMediaHealth(videoId, token);
                    break;
                } catch (error) {
                    lastError = error;
                    if (attempt + 1 < attempts) {
                        await new Promise((resolve) => window.setTimeout(resolve, 200));
                    }
                }
            }

            if (!response) throw lastError;
            if (
                mediaRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === videoId
                && response.VideoId === videoId
            ) {
                setMediaHealth(response);
                setMediaErrorMessage(null);
            }
        } catch {
            if (mediaRequestSequenceRef.current === requestSequence && activeVideoIdRef.current === videoId) {
                setMediaHealth(null);
                setMediaErrorMessage('Playback could not be checked. Video details are still available.');
            }
        } finally {
            if (mediaRequestSequenceRef.current === requestSequence && activeVideoIdRef.current === videoId) {
                setMediaLoading(false);
            }
        }
    }, [token]);

    const loadDetail = useCallback(async (videoId: string) => {
        const requestSequence = ++detailRequestSequenceRef.current;
        const editorEpoch = ++editorOperationEpochRef.current;
        activeUploadOperationRef.current = null;
        activeDraftOperationRef.current = null;
        participantModalOperationRef.current += 1;
        eventModalOperationRef.current += 1;
        venueModalOperationRef.current += 1;
        tagCreateOperationRef.current += 1;
        repairRequestSequenceRef.current += 1;
        replacementRequestSequenceRef.current += 1;
        activeVideoIdRef.current = videoId;
        mediaRequestSequenceRef.current += 1;
        setDetail(null);
        setSelectedVideoId(null);
        setForm({ ...defaultUpsertForm, Tags: [], Participants: [] });
        setMobilePlaybackOverride(null);
        setPresignedDeliveryOverride(null);
        savedPlaybackOverridesRef.current = { mobilePlaybackOverride: null, presignedDeliveryOverride: null };
        setEventSearchInput('');
        setLocationSearchInput('');
        setTagInput('');
        setParticipantSearchByIndex({});
        setFile(null);
        setUploadState(null);
        setMetadataSaveRetryRequired(false);
        setIsUploading(false);
        setIsSaving(false);
        setActiveTab('details');
        setAllowNavigationAfterSave(false);
        setErrorMessage(null);
        setSuccessMessage(null);
        setMediaHealth(null);
        setMediaErrorMessage(null);
        setMediaLoading(false);
        setRepairingFormat(null);
        setReplacementFile(null);
        setMediaAction(null);
        setIsCreateEventModalOpen(false);
        setIsCreatingEvent(false);
        setIsCreateParticipantModalOpen(false);
        setIsCreatingParticipant(false);
        setIsCreateVenueModalOpen(false);
        setIsCreatingVenue(false);
        try {
            const response = await fetchAdminVideo(videoId, token);
            if (detailRequestSequenceRef.current !== requestSequence || editorOperationEpochRef.current !== editorEpoch) {
                return;
            }
            setDetail(response);
            setSelectedVideoId(videoId);
            setForm(mapDetailToForm(response));
            setMobilePlaybackOverride(response.MobilePlaybackOverride ?? null);
            setPresignedDeliveryOverride(response.PresignedDeliveryOverride ?? null);
            savedPlaybackOverridesRef.current = {
                mobilePlaybackOverride: response.MobilePlaybackOverride ?? null,
                presignedDeliveryOverride: response.PresignedDeliveryOverride ?? null
            };
            setEventSearchInput(response.EventName ?? '');
            setLocationSearchInput(response.LocationName ?? '');
            setFile(null);
            setUploadState(null);
            setMetadataSaveRetryRequired(false);
            setActiveTab('details');
            void loadMediaHealth(videoId, true);
        } catch (error) {
            if (detailRequestSequenceRef.current === requestSequence && editorOperationEpochRef.current === editorEpoch) {
                activeVideoIdRef.current = videoId;
                setMediaHealth(null);
                setErrorMessage('Unable to load video details.');
            }
        }
    }, [loadMediaHealth, mapDetailToForm, token]);

    useEffect(() => {
        if (routeVideoId) {
            void loadDetail(routeVideoId);
        }
    }, [loadDetail, routeVideoId]);

    useEffect(() => {
        if (!isNewRoute) {
            setAllowNavigationAfterSave(false);
            return;
        }

        editorOperationEpochRef.current += 1;
        activeUploadOperationRef.current = null;
        activeDraftOperationRef.current = null;
        participantModalOperationRef.current += 1;
        eventModalOperationRef.current += 1;
        venueModalOperationRef.current += 1;
        tagCreateOperationRef.current += 1;
        detailRequestSequenceRef.current += 1;
        mediaRequestSequenceRef.current += 1;
        repairRequestSequenceRef.current += 1;
        replacementRequestSequenceRef.current += 1;
        activeVideoIdRef.current = null;
        setSelectedVideoId(null);
        setDetail(null);
        setMediaHealth(null);
        setMediaErrorMessage(null);
        setMediaLoading(false);
        setRepairingFormat(null);
        setReplacementFile(null);
        setMediaAction(null);
        setForm({ ...defaultUpsertForm, Tags: [], Participants: [] });
        setMobilePlaybackOverride(null);
        setPresignedDeliveryOverride(null);
        savedPlaybackOverridesRef.current = { mobilePlaybackOverride: null, presignedDeliveryOverride: null };
        setFile(null);
        setUploadState(null);
        setMetadataSaveRetryRequired(false);
        setIsUploading(false);
        setIsSaving(false);
        setAllowNavigationAfterSave(false);
        setErrorMessage(null);
        setSuccessMessage(null);
        setEventSearchInput('');
        setLocationSearchInput('');
        setTagInput('');
        setParticipantSearchByIndex({});
        setActiveTab('details');
        setIsCreateEventModalOpen(false);
        setIsCreatingEvent(false);
        setIsCreateParticipantModalOpen(false);
        setIsCreatingParticipant(false);
        setIsCreateVenueModalOpen(false);
        setIsCreatingVenue(false);
    }, [isNewRoute]);

    useEffect(() => () => {
        editorOperationEpochRef.current += 1;
        activeUploadOperationRef.current = null;
        activeDraftOperationRef.current = null;
        participantModalOperationRef.current += 1;
        eventModalOperationRef.current += 1;
        venueModalOperationRef.current += 1;
        tagCreateOperationRef.current += 1;
    }, []);

    const showFormValidationError = (validation: NonNullable<ReturnType<typeof validateVideoForm>>) => {
        setActiveTab(validation.tab);
        setSuccessMessage(null);
        setErrorMessage(validation.message);
        window.requestAnimationFrame(() => document.getElementById(validation.fieldId)?.focus());
    };

    const handleSave = async () => {
        if (!selectedVideoId) {
            setErrorMessage('Create the draft before saving later changes.');
            return;
        }
        if (isUploading) {
            setActiveTab('media');
            setErrorMessage('Wait for the source upload to finish before saving other changes.');
            return;
        }
        const savingVideoId = selectedVideoId;
        const editorEpoch = editorOperationEpochRef.current;
        const validation = validateVideoForm(form);
        if (validation) {
            showFormValidationError(validation);
            return;
        }
        if (form.PublishStatus === 1 && detail?.PublishStatus !== 1 && !isReadyToPublish) {
            setActiveTab('media');
            setSuccessMessage(null);
            setErrorMessage('This video cannot be published yet. Upload the source and wait until playable media is Ready.');
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            if (isSuperAdminUser && presignedDeliveryOverride === false && !apiStreamFallbackEnabled) {
                setActiveTab('super-admin');
                setErrorMessage('Force API stream requires API stream fallback to be enabled in Super Admin video playback settings.');
                return;
            }

            const submittedForm = cloneForm(form);
            const payload = normalizeUpsertPayload(submittedForm);

            let response = await upsertAdminVideo(savingVideoId, payload, token);
            if (editorOperationEpochRef.current !== editorEpoch || activeVideoIdRef.current !== savingVideoId) {
                return;
            }
            const playbackOverridesDirty = isSuperAdminUser && (
                savedPlaybackOverridesRef.current.mobilePlaybackOverride !== mobilePlaybackOverride
                || savedPlaybackOverridesRef.current.presignedDeliveryOverride !== presignedDeliveryOverride
            );
            if (playbackOverridesDirty) {
                try {
                    response = await updateAdminVideoPlaybackConfiguration(
                        savingVideoId,
                        {
                            MobilePlaybackOverride: mobilePlaybackOverride,
                            PresignedDeliveryOverride: presignedDeliveryOverride
                        },
                        token
                    );
                } catch {
                    if (editorOperationEpochRef.current !== editorEpoch || activeVideoIdRef.current !== savingVideoId) {
                        return;
                    }
                    setDetail(response);
                    setForm(mapDetailToForm(response));
                    setErrorMessage('Video details saved, but the Super Admin playback overrides could not be saved.');
                    return;
                }
            }
            if (editorOperationEpochRef.current !== editorEpoch || activeVideoIdRef.current !== savingVideoId) {
                return;
            }

            const savedResponseForm = mapDetailToForm(response);
            const nextForm = JSON.stringify(formRef.current) === JSON.stringify(submittedForm)
                ? savedResponseForm
                : cloneForm(formRef.current);
            setDetail(response);
            formRef.current = nextForm;
            setForm(nextForm);
            setMobilePlaybackOverride(response.MobilePlaybackOverride ?? null);
            setPresignedDeliveryOverride(response.PresignedDeliveryOverride ?? null);
            savedPlaybackOverridesRef.current = {
                mobilePlaybackOverride: response.MobilePlaybackOverride ?? null,
                presignedDeliveryOverride: response.PresignedDeliveryOverride ?? null
            };
            setSuccessMessage('Video details saved.');
            setMetadataSaveRetryRequired(false);
            if (isNewRoute && !uploadState && !file) {
                navigateToSavedDraft(savingVideoId, editorEpoch);
            }
        } catch (error) {
            if (editorOperationEpochRef.current === editorEpoch && activeVideoIdRef.current === savingVideoId) {
                const status = (error as { status?: number }).status;
                if (status === 409 && form.PublishStatus === 1 && detail?.PublishStatus !== 1) {
                    setActiveTab('media');
                    setErrorMessage(error instanceof Error
                        ? error.message
                        : 'This video is not ready to publish. Refresh Media and resolve the missing playback checks.');
                    void loadMediaHealth(savingVideoId, true);
                } else {
                    setErrorMessage(error instanceof Error ? error.message : 'Unable to save video metadata.');
                }
            }
        } finally {
            if (editorOperationEpochRef.current === editorEpoch && activeVideoIdRef.current === savingVideoId) {
                setIsSaving(false);
            }
        }
    };

    const handleRepairMedia = async (mediaVideoId: string, fileHealth: AdminVideoMediaFileDto) => {
        if (!selectedVideoId || selectedVideoId !== mediaVideoId) {
            setErrorMessage('The selected video changed. Refresh its media health before repairing.');
            return;
        }
        if (fileHealth.Format !== videoFormat.primaryPlayback && fileHealth.Format !== videoFormat.mobilePlayback) {
            setErrorMessage('Only primary and mobile MP4 playback can be generated or retried here. WebM playback is read-only.');
            return;
        }

        const requestSequence = ++repairRequestSequenceRef.current;
        setRepairingFormat(fileHealth.Format);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const result = await repairAdminVideoMedia(mediaVideoId, fileHealth.Format, token);
            if (activeVideoIdRef.current !== mediaVideoId) {
                return;
            }

            setSuccessMessage(result.Message);
            await loadMediaHealth(mediaVideoId);
        } catch (error) {
            if (activeVideoIdRef.current === mediaVideoId) {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to queue media repair.');
            }
        } finally {
            if (repairRequestSequenceRef.current === requestSequence && activeVideoIdRef.current === mediaVideoId) {
                setRepairingFormat(null);
            }
        }
    };

    const getReplacementPayload = (selectedFile: File) => ({
        Filename: selectedFile.name,
        FileSize: selectedFile.size,
        ContentType: 'video/mp4'
    });

    const handleReplacementUpload = async (generationId?: string) => {
        const mediaVideoId = mediaHealth?.VideoId;
        if (!mediaVideoId || selectedVideoId !== mediaVideoId) {
            setErrorMessage('The selected video changed. Refresh its media health before replacing the source.');
            return;
        }

        if (!generationId && hasMetadataUnsavedChanges) {
            setErrorMessage('Save metadata changes before starting a source replacement.');
            return;
        }

        if (!replacementFile) {
            setErrorMessage('Select an MP4 replacement file first.');
            return;
        }

        if (!isMp4VideoFile(replacementFile)) {
            setErrorMessage('Replacement source must be an MP4 video.');
            return;
        }

        const isInitialUploadRetry = Boolean(generationId && !mediaHealth.ActiveGeneration);
        const confirmation = isInitialUploadRetry
            ? 'Retry this initial source upload? A fresh secure upload link will be created and processing starts only after the new upload is verified.'
            : generationId
            ? 'Retry this candidate upload? The current playback remains live and unchanged while the candidate is processed.'
            : 'Replace this video source? The existing video, URL, and metadata are retained. Current playback remains live until replacement primary processing succeeds. Existing and failed replacement files are retained, and mobile processing happens separately.';
        if (!window.confirm(confirmation)) {
            return;
        }

        const requestSequence = ++replacementRequestSequenceRef.current;
        setMediaAction(generationId ? 'retry-upload' : 'replace-source');
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const upload = generationId
                ? await createAdminVideoMediaReplacementUploadUrl(
                    mediaVideoId,
                    generationId,
                    getReplacementPayload(replacementFile),
                    token
                )
                : await createAdminVideoMediaReplacement(
                    mediaVideoId,
                    getReplacementPayload(replacementFile),
                    token
                );
            await uploadToPresignedUrl(upload.PresignedUrl, replacementFile);
            const result = await completeAdminVideoMediaReplacementUpload(
                mediaVideoId,
                upload.GenerationId,
                upload.UploadAttemptId,
                token
            );
            if (!result.Completed) {
                throw new Error(result.Message);
            }

            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setReplacementFile(null);
                setFile((current) => current === replacementFile ? null : current);
                setSuccessMessage(result.Message);
                await loadMediaHealth(mediaVideoId);
            }
        } catch (error) {
            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to upload the replacement source.');
                await loadMediaHealth(mediaVideoId);
            }
        } finally {
            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setMediaAction(null);
            }
        }
    };

    const handleRetryReplacementPrimary = async (generationId: string) => {
        const mediaVideoId = mediaHealth?.VideoId;
        if (!mediaVideoId || selectedVideoId !== mediaVideoId) {
            setErrorMessage('The selected video changed. Refresh its media health before retrying processing.');
            return;
        }

        const requestSequence = ++replacementRequestSequenceRef.current;
        setMediaAction('retry-primary');
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const result = mediaHealth.ActiveGeneration
                ? await retryAdminVideoMediaReplacementPrimary(mediaVideoId, generationId, token)
                : await repairAdminVideoMedia(mediaVideoId, videoFormat.primaryPlayback, token);
            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setSuccessMessage(result.Message);
                await loadMediaHealth(mediaVideoId);
            }
        } catch (error) {
            if (activeVideoIdRef.current === mediaVideoId) {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to retry primary processing.');
            }
        } finally {
            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setMediaAction(null);
            }
        }
    };

    const handleAbandonReplacement = async (generationId: string) => {
        const mediaVideoId = mediaHealth?.VideoId;
        if (!mediaVideoId || selectedVideoId !== mediaVideoId) {
            setErrorMessage('The selected video changed. Refresh its media health before abandoning the replacement.');
            return;
        }

        if (!window.confirm('Abandon this candidate replacement? Its retained storage objects will no longer be processed, and current playback remains unchanged.')) {
            return;
        }

        const requestSequence = ++replacementRequestSequenceRef.current;
        setMediaAction('abandon');
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const result = await abandonAdminVideoMediaReplacement(mediaVideoId, generationId, token);
            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setReplacementFile(null);
                setSuccessMessage(result.Message);
                await loadMediaHealth(mediaVideoId);
            }
        } catch (error) {
            if (activeVideoIdRef.current === mediaVideoId) {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to abandon the replacement.');
            }
        } finally {
            if (
                replacementRequestSequenceRef.current === requestSequence
                && activeVideoIdRef.current === mediaVideoId
            ) {
                setMediaAction(null);
            }
        }
    };

    const cloneForm = (value: AdminVideoUpsertDto): AdminVideoUpsertDto => ({
        ...value,
        Tags: [...value.Tags],
        Participants: value.Participants.map((participant) => ({ ...participant }))
    });

    const navigateToSavedDraft = (videoId: string, editorEpoch: number) => {
        pendingSavedDraftNavigationRef.current = { videoId, editorEpoch };
        setAllowNavigationAfterSave(true);
    };

    const canLeaveNewDraft = (savedDraft: AdminVideoUpsertDto) => (
        JSON.stringify(formRef.current) === JSON.stringify(savedDraft)
        && savedPlaybackOverridesRef.current.mobilePlaybackOverride === mobilePlaybackOverride
        && savedPlaybackOverridesRef.current.presignedDeliveryOverride === presignedDeliveryOverride
    );

    const uploadInitialSource = async (
        videoId: string,
        uploadFile: File,
        editorEpoch: number,
        savedDraft?: AdminVideoUpsertDto
    ) => {
        if (activeUploadOperationRef.current !== null) return;
        if (!isMp4VideoFile(uploadFile)) {
            setActiveTab('media');
            setErrorMessage('Source video must be an MP4 file. Choose an .mp4 file before uploading the source.');
            return;
        }

        const uploadOperationId = ++uploadOperationSequenceRef.current;
        activeUploadOperationRef.current = uploadOperationId;
        setIsUploading(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        let uploadCreated = false;
        try {
            const upload = await createAdminVideoMediaReplacement(
                videoId,
                getReplacementPayload(uploadFile),
                token
            );
            if (
                editorOperationEpochRef.current !== editorEpoch
                || activeUploadOperationRef.current !== uploadOperationId
                || activeVideoIdRef.current !== videoId
            ) return;
            uploadCreated = true;
            setUploadState({
                videoId,
                mediaGenerationId: upload.GenerationId,
                uploadAttemptId: upload.UploadAttemptId,
                presignedUrl: upload.PresignedUrl,
                file: uploadFile
            });

            await uploadToPresignedUrl(upload.PresignedUrl, uploadFile);
            if (
                editorOperationEpochRef.current !== editorEpoch
                || activeUploadOperationRef.current !== uploadOperationId
                || activeVideoIdRef.current !== videoId
            ) return;
            const completion = await completeAdminVideoMediaReplacementUpload(
                videoId,
                upload.GenerationId,
                upload.UploadAttemptId,
                token
            );
            if (
                editorOperationEpochRef.current !== editorEpoch
                || activeUploadOperationRef.current !== uploadOperationId
                || activeVideoIdRef.current !== videoId
            ) return;
            if (!completion.Completed) throw new Error(completion.Message);

            setUploadState(null);
            setFile((current) => current === uploadFile ? null : current);
            setSuccessMessage('Source uploaded. Primary playback processing has started.');
            await loadMediaHealth(videoId, true);
            if (savedDraft && isNewRoute && canLeaveNewDraft(savedDraft)) {
                navigateToSavedDraft(videoId, editorEpoch);
            }
        } catch (error) {
            if (
                editorOperationEpochRef.current === editorEpoch
                && activeUploadOperationRef.current === uploadOperationId
                && activeVideoIdRef.current === videoId
            ) {
                setErrorMessage(uploadCreated
                    ? 'The draft is saved, but the source upload did not complete. Your file remains selected; use Resume source upload to continue.'
                    : error instanceof Error ? error.message : 'Unable to start the source upload.');
                if (!uploadCreated) {
                    await loadMediaHealth(videoId, true);
                }
            }
        } finally {
            if (activeUploadOperationRef.current === uploadOperationId) {
                activeUploadOperationRef.current = null;
                if (editorOperationEpochRef.current === editorEpoch) setIsUploading(false);
            }
        }
    };

    const handleCreateDraft = async () => {
        if (activeDraftOperationRef.current !== null || selectedVideoId) return;
        const validation = validateVideoForm(form);
        if (validation) {
            showFormValidationError(validation);
            return;
        }
        if (form.PublishStatus === 1) {
            setActiveTab('publishing');
            setErrorMessage('Create the draft as Not published. It can be published after playable media is ready.');
            return;
        }
        if (file && !isMp4VideoFile(file)) {
            setActiveTab('media');
            setErrorMessage('Source video must be an MP4 file. Choose an .mp4 file or clear the selection before creating the draft.');
            return;
        }

        const editorEpoch = editorOperationEpochRef.current;
        const requestId = ++draftOperationSequenceRef.current;
        activeDraftOperationRef.current = requestId;
        const submittedDraft = normalizeUpsertPayload(cloneForm({
            ...form,
            EventType: form.EventType.trim() || 'General',
            PublishStatus: 0
        }));
        const selectedSource = file;
        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const response = await createAdminVideoDraft(submittedDraft, token);
            if (editorOperationEpochRef.current !== editorEpoch || activeDraftOperationRef.current !== requestId) return;

            const savedDraft = mapDetailToForm(response);
            const currentForm = formRef.current;
            const nextForm = JSON.stringify(currentForm) === JSON.stringify(form)
                ? savedDraft
                : cloneForm(currentForm);
            activeVideoIdRef.current = response.Id;
            setSelectedVideoId(response.Id);
            setDetail(response);
            formRef.current = nextForm;
            setForm(nextForm);
            setMetadataSaveRetryRequired(false);
            setSuccessMessage(selectedSource ? 'Draft created. Uploading the selected source…' : 'Draft created. You can add the source from Media when ready.');

            if (selectedSource) {
                await uploadInitialSource(response.Id, selectedSource, editorEpoch, savedDraft);
            } else if (canLeaveNewDraft(savedDraft)) {
                navigateToSavedDraft(response.Id, editorEpoch);
            }
        } catch (error) {
            if (editorOperationEpochRef.current === editorEpoch && activeDraftOperationRef.current === requestId) {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to create the video draft.');
            }
        } finally {
            if (activeDraftOperationRef.current === requestId) {
                activeDraftOperationRef.current = null;
                if (editorOperationEpochRef.current === editorEpoch) setIsSaving(false);
            }
        }
    };

    const handleUploadSource = async () => {
        if (!selectedVideoId) {
            setErrorMessage('Create the draft before uploading its source.');
            return;
        }
        if (!file) {
            setActiveTab('media');
            setErrorMessage('Select an MP4 source file first.');
            return;
        }
        await uploadInitialSource(selectedVideoId, file, editorOperationEpochRef.current, detail ? mapDetailToForm(detail) : undefined);
    };

    const handleRetryUpload = async () => {
        if (!uploadState || activeUploadOperationRef.current !== null) {
            return;
        }

        const pendingUpload = uploadState;
        const retryFile = file ?? pendingUpload.file;
        const fileChanged = file != null && file !== pendingUpload.file;
        if (!isMp4VideoFile(retryFile)) {
            setActiveTab('media');
            setErrorMessage('Source video must be an MP4 file. Choose an .mp4 file before resuming the upload.');
            return;
        }
        if (fileChanged && !pendingUpload.mediaGenerationId) {
            setActiveTab('media');
            setErrorMessage('This pending video cannot safely switch to a different file because its upload generation is unavailable. Re-select the original file or resume it from Videos.');
            return;
        }

        const editorEpoch = editorOperationEpochRef.current;
        const uploadOperationId = ++uploadOperationSequenceRef.current;
        activeUploadOperationRef.current = uploadOperationId;
        setIsUploading(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            let completion;
            if (!fileChanged) {
                try {
                    completion = await completeAdminVideoMediaReplacementUpload(
                        pendingUpload.videoId,
                        pendingUpload.mediaGenerationId ?? '',
                        pendingUpload.uploadAttemptId ?? '',
                        token
                    );
                } catch (error) {
                    if (editorOperationEpochRef.current !== editorEpoch || activeUploadOperationRef.current !== uploadOperationId) {
                        return;
                    }
                    if ((error as { status?: number }).status !== 400) {
                        setErrorMessage('Unable to confirm the previous upload yet. No new upload was started; retry again to safely check it.');
                        return;
                    }
                }
                if (editorOperationEpochRef.current !== editorEpoch || activeUploadOperationRef.current !== uploadOperationId) {
                    return;
                }
            }

            if (completion?.Completed) {
                setUploadState(null);
                setFile((current) => current === retryFile ? null : current);
                setSuccessMessage('Source upload confirmed. Primary playback processing has started.');
                await loadMediaHealth(pendingUpload.videoId, true);
                const savedDraft = detail ? mapDetailToForm(detail) : null;
                if (savedDraft && isNewRoute && canLeaveNewDraft(savedDraft)) {
                    navigateToSavedDraft(pendingUpload.videoId, editorEpoch);
                }
                return;
            }

            const refreshedUpload = pendingUpload.mediaGenerationId
                ? await createAdminVideoMediaReplacementUploadUrl(
                    pendingUpload.videoId,
                    pendingUpload.mediaGenerationId,
                    getReplacementPayload(retryFile),
                    token
                )
                : null;
            if (editorOperationEpochRef.current !== editorEpoch || activeUploadOperationRef.current !== uploadOperationId) {
                return;
            }
            const presignedUrl = refreshedUpload?.PresignedUrl ?? pendingUpload.presignedUrl;
            const uploadAttemptId = refreshedUpload?.UploadAttemptId ?? pendingUpload.uploadAttemptId;
            if (refreshedUpload) {
                setUploadState((previous) => previous ? {
                    ...previous,
                    mediaGenerationId: refreshedUpload.GenerationId,
                    uploadAttemptId: refreshedUpload.UploadAttemptId,
                    presignedUrl: refreshedUpload.PresignedUrl,
                    file: retryFile
                } : previous);
            }
            await uploadToPresignedUrl(presignedUrl, retryFile);
            if (editorOperationEpochRef.current !== editorEpoch || activeUploadOperationRef.current !== uploadOperationId) {
                return;
            }
            completion = await completeAdminVideoMediaReplacementUpload(
                pendingUpload.videoId,
                refreshedUpload?.GenerationId ?? pendingUpload.mediaGenerationId ?? '',
                uploadAttemptId ?? '',
                token
            );
            if (editorOperationEpochRef.current !== editorEpoch || activeUploadOperationRef.current !== uploadOperationId) {
                return;
            }
            if (!completion.Completed) {
                throw new Error(completion.Message);
            }
            setUploadState(null);
            setFile((current) => current === retryFile ? null : current);
            setSuccessMessage('Source uploaded. Primary playback processing has started.');
            await loadMediaHealth(pendingUpload.videoId, true);
            const savedDraft = detail ? mapDetailToForm(detail) : null;
            if (savedDraft && isNewRoute && canLeaveNewDraft(savedDraft)) {
                navigateToSavedDraft(pendingUpload.videoId, editorEpoch);
            }
        } catch (error) {
            if (editorOperationEpochRef.current === editorEpoch && activeUploadOperationRef.current === uploadOperationId) {
                setErrorMessage('The draft is safe and the selected file is retained, but the source upload retry failed. Try again.');
            }
        } finally {
            if (activeUploadOperationRef.current === uploadOperationId) {
                activeUploadOperationRef.current = null;
                if (editorOperationEpochRef.current === editorEpoch) {
                    setIsUploading(false);
                }
            }
        }
    };

    const handleCopyShortcode = async () => {
        const shortcode = detail?.Url?.trim() ?? '';
        if (!shortcode) {
            setErrorMessage('No shortcode available to copy.');
            return;
        }

        const copied = await copyTextToClipboard(shortcode);
        if (copied) {
            setErrorMessage(null);
            setSuccessMessage('Shortcode copied to clipboard.');
            return;
        }

        setSuccessMessage(null);
        setErrorMessage('Unable to copy shortcode. Please copy it manually.');
    };

    const addParticipantAssignment = () => {
        const next = [...form.Participants, { ParticipantId: '', TypeId: '', CornerColor: null }];
        setForm({ ...form, Participants: next });
    };

    const removeParticipantAssignment = (index: number) => {
        const next = form.Participants.filter((_, idx) => idx !== index);
        setForm({ ...form, Participants: next });
        setParticipantSearchByIndex((previous) => {
            const shifted: Record<number, string> = {};
            Object.entries(previous).forEach(([key, value]) => {
                const currentIndex = Number(key);
                if (currentIndex < index) {
                    shifted[currentIndex] = value;
                } else if (currentIndex > index) {
                    shifted[currentIndex - 1] = value;
                }
            });
            return shifted;
        });
    };

    const updateParticipantAssignment = (
        index: number,
        field: keyof AdminVideoParticipantAssignmentDto,
        value: string
    ) => {
        const next = [...form.Participants];
        if (field === 'CornerColor') {
            next[index] = { ...next[index], CornerColor: normalizeCornerColor(value) };
        } else {
            next[index] = { ...next[index], [field]: value };
        }

        setForm({ ...form, Participants: next });
    };

    const handleOpenCreateParticipantModal = (assignmentIndex: number) => {
        participantModalOperationRef.current += 1;
        const prefillName = (participantSearchByIndex[assignmentIndex] ?? '').trim().slice(0, 200);
        setCreateParticipantAssignmentIndex(assignmentIndex);
        setCreateParticipantForm({
            ...emptyParticipantForm,
            Name: prefillName
        });
        setCreateParticipantError(null);
        setIsCreateParticipantModalOpen(true);
    };

    const handleCloseCreateParticipantModal = () => {
        participantModalOperationRef.current += 1;
        setIsCreateParticipantModalOpen(false);
        setIsCreatingParticipant(false);
        setCreateParticipantAssignmentIndex(null);
        setCreateParticipantForm(emptyParticipantForm);
        setCreateParticipantError(null);
    };

    const handleCreateParticipantFromModal = async (event: React.FormEvent) => {
        event.preventDefault();

        if (createParticipantAssignmentIndex == null || isCreatingParticipant) {
            return;
        }

        const assignmentIndex = createParticipantAssignmentIndex;
        const editorEpoch = editorOperationEpochRef.current;
        const requestId = ++participantModalOperationRef.current;
        const requestIsCurrent = () => (
            participantModalOperationRef.current === requestId
            && editorOperationEpochRef.current === editorEpoch
        );

        const payload = normalizeParticipantCreatePayload(createParticipantForm);
        if (!payload.Name || payload.Name.trim().length === 0) {
            setCreateParticipantError('Participant name is required.');
            return;
        }

        setIsCreatingParticipant(true);
        setCreateParticipantError(null);
        setErrorMessage(null);
        setSuccessMessage(null);

        let participantCreated = false;
        try {
            await createParticipant(payload, token);
            participantCreated = true;
            if (!requestIsCurrent()) return;
            const participantRequestSequence = ++participantLookupRequestSequenceRef.current;
            const refreshedParticipants = await fetchParticipants(token);
            if (!requestIsCurrent()) return;
            if (participantLookupRequestSequenceRef.current === participantRequestSequence) {
                setParticipants(refreshedParticipants);
            }

            const targetName = payload.Name.trim().toLowerCase();
            const targetNickname = (payload.Nickname ?? '').trim().toLowerCase();
            const createdParticipant = refreshedParticipants.find((item) => {
                const itemName = (item.Name ?? '').trim().toLowerCase();
                const itemNickname = (item.Nickname ?? '').trim().toLowerCase();
                if (itemName !== targetName) {
                    return false;
                }

                if (!targetNickname) {
                    return true;
                }

                return itemNickname === targetNickname;
            });

            if (!createdParticipant) {
                setCreateParticipantError('Participant created, but could not auto-select. Please search and select manually.');
                return;
            }

            setForm((previous) => {
                const nextParticipants = [...previous.Participants];
                const selectedAssignment = nextParticipants[assignmentIndex];
                if (!selectedAssignment) {
                    return previous;
                }

                nextParticipants[assignmentIndex] = {
                    ...selectedAssignment,
                    ParticipantId: createdParticipant.Id
                };

                return {
                    ...previous,
                    Participants: nextParticipants
                };
            });

            setParticipantSearchByIndex((previous) => ({
                ...previous,
                [assignmentIndex]: createdParticipant.Nickname
                    ? `${createdParticipant.Name} (${createdParticipant.Nickname})`
                    : createdParticipant.Name
            }));

            setSuccessMessage(`Participant "${createdParticipant.Name}" created and selected.`);
            handleCloseCreateParticipantModal();
        } catch (error) {
            if (requestIsCurrent()) {
                setCreateParticipantError(participantCreated
                    ? 'Participant created, but the participant list could not be refreshed. Do not create it again; close this dialog and reopen the editor before selecting it.'
                    : error instanceof Error ? error.message : 'Unable to create participant.');
            }
        } finally {
            if (requestIsCurrent()) setIsCreatingParticipant(false);
        }
    };

    const handleOpenCreateEventModal = () => {
        eventModalOperationRef.current += 1;
        const prefillName = eventSearchInput.trim() || selectedEvent?.name || '';
        setCreateEventForm({
            ...emptyEventForm,
            name: prefillName.slice(0, 200)
        });
        setCreateEventLocationSearchInput('');
        setCreateEventError(null);
        setIsCreateEventModalOpen(true);
    };

    const handleCloseCreateEventModal = () => {
        eventModalOperationRef.current += 1;
        if (isCreateVenueModalOpen && createVenueTarget === 'event-modal') {
            venueModalOperationRef.current += 1;
            setIsCreateVenueModalOpen(false);
            setIsCreatingVenue(false);
            setCreateVenueTarget('video-form');
            setCreateVenueForm(emptyVenueForm);
            setCreateVenueError(null);
        }
        setIsCreateEventModalOpen(false);
        setIsCreatingEvent(false);
        setCreateEventForm(emptyEventForm);
        setCreateEventLocationSearchInput('');
        setCreateEventError(null);
    };

    const handleCreateEventFromModal = async (event: React.FormEvent) => {
        event.preventDefault();

        if (isCreatingEvent) return;

        const normalized = normalizeEventCreatePayload(createEventForm);
        if (!normalized.name) {
            setCreateEventError('Event name is required.');
            return;
        }

        const eventDateUtc = toUtcIsoString(normalized.eventDate);
        if (!eventDateUtc) {
            setCreateEventError('Event date is required.');
            return;
        }

        setIsCreatingEvent(true);
        setCreateEventError(null);
        setErrorMessage(null);
        setSuccessMessage(null);

        const editorEpoch = editorOperationEpochRef.current;
        const requestId = ++eventModalOperationRef.current;
        const requestIsCurrent = () => (
            eventModalOperationRef.current === requestId
            && editorOperationEpochRef.current === editorEpoch
        );

        try {
            const createdEventResponse = await createAdminEvent({
                ...normalized,
                eventDate: eventDateUtc
            }, token);
            if (!requestIsCurrent()) return;
            const selectedCreatedEvent = mapEvent(createdEventResponse, {
                name: normalized.name,
                description: normalized.description,
                eventDate: eventDateUtc,
                eventUrl: normalized.eventUrl,
                thumbnailUrl: normalized.thumbnailUrl,
                locationId: normalized.locationId,
                tags: normalized.tags,
                hideFromFightCards: normalized.hideFromFightCards
            });
            if (!selectedCreatedEvent.id) throw new Error('Event was created without an identifier. Reopen the editor before selecting it.');

            const eventRequestSequence = ++eventLookupRequestSequenceRef.current;
            setEvents((previous) => [selectedCreatedEvent, ...previous.filter((item) => item.id !== selectedCreatedEvent.id)]);
            setForm((previous) => ({
                ...previous,
                EventId: selectedCreatedEvent.id,
                LocationId: null
            }));
            setEventSearchInput(selectedCreatedEvent.name);
            setLocationSearchInput('');
            setSuccessMessage(`Event "${selectedCreatedEvent.name}" created and selected.`);
            handleCloseCreateEventModal();

            try {
                const refreshedEvents = (await fetchAdminEvents(true, token)).map((item: any) => mapEvent(item));
                if (
                    editorOperationEpochRef.current === editorEpoch
                    && eventLookupRequestSequenceRef.current === eventRequestSequence
                ) {
                    setEvents(refreshedEvents.some((item) => item.id === selectedCreatedEvent.id)
                        ? refreshedEvents
                        : [selectedCreatedEvent, ...refreshedEvents]);
                }
            } catch {
                if (editorOperationEpochRef.current === editorEpoch) {
                    setSuccessMessage(`Event "${selectedCreatedEvent.name}" was created and selected. The lookup refresh failed, so other new events may appear after reopening the editor.`);
                }
            }
        } catch (error) {
            if (requestIsCurrent()) {
                setCreateEventError(error instanceof Error ? error.message : 'Unable to create event.');
            }
        } finally {
            if (requestIsCurrent()) setIsCreatingEvent(false);
        }
    };

    const handleOpenCreateVenueModal = (target: CreateVenueTarget = 'video-form') => {
        if (target === 'video-form' && form.EventId) {
            return;
        }

        venueModalOperationRef.current += 1;
        setCreateVenueTarget(target);
        setCreateVenueForm({
            ...emptyVenueForm,
            name: (target === 'event-modal' ? createEventLocationSearchInput : locationSearchInput).trim().slice(0, 200)
        });
        setCreateVenueError(null);
        setIsCreateVenueModalOpen(true);
    };

    const handleCloseCreateVenueModal = () => {
        venueModalOperationRef.current += 1;
        setIsCreateVenueModalOpen(false);
        setIsCreatingVenue(false);
        setCreateVenueTarget('video-form');
        setCreateVenueForm(emptyVenueForm);
        setCreateVenueError(null);
    };

    const handleCreateVenueFromModal = async (event: React.FormEvent) => {
        event.preventDefault();

        if (isCreatingVenue) return;

        const venueTarget = createVenueTarget;

        if (venueTarget === 'video-form' && form.EventId) {
            setCreateVenueError('Venue is derived from event. Clear event selection to edit venue.');
            return;
        }

        const payload = normalizeVenueCreatePayload(createVenueForm);
        if (!payload.name) {
            setCreateVenueError('Venue name is required.');
            return;
        }

        setIsCreatingVenue(true);
        setCreateVenueError(null);
        setErrorMessage(null);
        setSuccessMessage(null);

        const editorEpoch = editorOperationEpochRef.current;
        const requestId = ++venueModalOperationRef.current;
        const requestIsCurrent = () => (
            venueModalOperationRef.current === requestId
            && editorOperationEpochRef.current === editorEpoch
        );

        try {
            const createdLocation = mapLocation(await createAdminLocation(payload, token));
            if (!requestIsCurrent()) return;
            if (!createdLocation.id) throw new Error('Venue was created without an identifier. Reopen the editor before selecting it.');
            const locationRequestSequence = ++locationLookupRequestSequenceRef.current;
            setLocations((previous) => [createdLocation, ...previous.filter((item) => item.id !== createdLocation.id)]);

            if (venueTarget === 'event-modal') {
                setCreateEventForm((previous) => ({
                    ...previous,
                    locationId: createdLocation.id
                }));
                setCreateEventLocationSearchInput(createdLocation.name);
                setSuccessMessage(`Venue "${createdLocation.name}" created and selected for event.`);
            } else {
                setForm((previous) => ({
                    ...previous,
                    LocationId: createdLocation.id
                }));
                setLocationSearchInput(createdLocation.name);
                setSuccessMessage(`Venue "${createdLocation.name}" created and selected.`);
            }

            handleCloseCreateVenueModal();

            try {
                const refreshedLocations = (await fetchAdminLocations(true, token)).map((item: any) => mapLocation(item));
                if (
                    editorOperationEpochRef.current === editorEpoch
                    && locationLookupRequestSequenceRef.current === locationRequestSequence
                ) {
                    setLocations(refreshedLocations.some((item) => item.id === createdLocation.id)
                        ? refreshedLocations
                        : [createdLocation, ...refreshedLocations]);
                }
            } catch {
                if (editorOperationEpochRef.current === editorEpoch) {
                    setSuccessMessage(venueTarget === 'event-modal'
                        ? `Venue "${createdLocation.name}" was created and selected for the event. The lookup refresh failed, so other new venues may appear after reopening the editor.`
                        : `Venue "${createdLocation.name}" was created and selected. The lookup refresh failed, so other new venues may appear after reopening the editor.`);
                }
            }
        } catch (error) {
            if (requestIsCurrent()) {
                setCreateVenueError(error instanceof Error ? error.message : 'Unable to create venue.');
            }
        } finally {
            if (requestIsCurrent()) setIsCreatingVenue(false);
        }
    };

    const availableTags = useMemo(
        () => tags.filter((tag) => {
            const tagName = tag?.name ?? '';
            if (!tagName.trim()) {
                return false;
            }

            return !form.Tags.some((value) => normalizeName(value) === normalizeName(tagName));
        }),
        [tags, form.Tags]
    );

    const eventTagSummary = useMemo(() => {
        const eventTags = detail?.EventTags ?? [];
        const preview = eventTags.slice(0, 2);
        return {
            eventTags,
            preview,
            remaining: Math.max(0, eventTags.length - preview.length)
        };
    }, [detail?.EventTags]);

    const eventTagLink = detail?.EventId
        ? `${Routes.ADMIN_EVENTS}?eventId=${encodeURIComponent(detail.EventId)}`
        : null;

    const addExistingTagByName = (value: string): boolean => {
        const trimmed = value.trim();
        if (!trimmed) {
            return false;
        }

        const normalized = normalizeName(trimmed);
        if (form.Tags.some((tag) => normalizeName(tag) === normalized)) {
            setTagInput('');
            return true;
        }

        const existing = tags.find((tag) => normalizeName(tag?.name) === normalized);
        if (!existing) {
            return false;
        }

        const existingName = existing.name ?? '';
        if (!existingName.trim()) {
            return false;
        }

        setErrorMessage(null);
        setForm({ ...form, Tags: [...form.Tags, existingName] });
        setTagInput('');
        return true;
    };

    const createTag = async () => {
        const trimmed = tagInput.trim();
        if (!trimmed) {
            return;
        }

        setErrorMessage(null);
        if (addExistingTagByName(trimmed)) {
            return;
        }

        const editorEpoch = editorOperationEpochRef.current;
        const requestId = ++tagCreateOperationRef.current;
        const requestIsCurrent = () => (
            tagCreateOperationRef.current === requestId
            && editorOperationEpochRef.current === editorEpoch
        );

        try {
            const created = await createAdminTag({ name: trimmed }, token);
            if (!requestIsCurrent()) return;
            const createdName = created?.name ?? '';
            if (!createdName.trim()) {
                setErrorMessage('Unable to add tag.');
                return;
            }

            const normalizedCreated = mapTag(created);
            const tagRequestSequence = ++tagLookupRequestSequenceRef.current;
            setTags((previous) => [...previous.filter((item) => normalizeName(item.name) !== normalizeName(createdName)), normalizedCreated]);
            setForm((previous) => ({
                ...previous,
                Tags: [...previous.Tags, createdName]
            }));
            setTagInput('');

            try {
                const refreshedTags = (await fetchAdminTags(token) ?? []).map((item: any) => mapTag(item));
                if (requestIsCurrent() && tagLookupRequestSequenceRef.current === tagRequestSequence) {
                    setTags(refreshedTags.some((item) => item.id === normalizedCreated.id || normalizeName(item.name) === normalizeName(createdName))
                        ? refreshedTags
                        : [normalizedCreated, ...refreshedTags]);
                }
            } catch {
                if (requestIsCurrent()) {
                    setSuccessMessage(`Tag "${createdName}" was created and selected. The lookup refresh failed, so other new tags may appear after reopening the editor.`);
                }
            }
        } catch (error) {
            if (requestIsCurrent()) setErrorMessage('Unable to add tag.');
        }
    };

    const removeTag = (value: string) => {
        setForm({
            ...form,
            Tags: form.Tags.filter((tag) => tag !== value)
        });
    };

    const isCreateMode = !selectedVideoId;
    const canEditLocation = !form.EventId;
    const canCreateDraft = form.Title.trim().length > 0 && !isSaving && !isUploading;
    const locationState = (location.state ?? {}) as { videosReturnTo?: string };
    const returnToVideos = () => navigate(locationState.videosReturnTo ?? Routes.ADMIN_VIDEOS);
    const savedForm = detail ? mapDetailToForm(detail) : null;
    const hasCreateDraft = JSON.stringify(form) !== JSON.stringify(defaultUpsertForm) || Boolean(file);
    const hasMetadataUnsavedChanges = isCreateMode
        ? hasCreateDraft
        : metadataSaveRetryRequired || (Boolean(savedForm) && (
            JSON.stringify(form) !== JSON.stringify(savedForm)
            || savedPlaybackOverridesRef.current.mobilePlaybackOverride !== mobilePlaybackOverride
            || savedPlaybackOverridesRef.current.presignedDeliveryOverride !== presignedDeliveryOverride
        ));
    const hasUnsavedChanges = hasMetadataUnsavedChanges || Boolean(file) || Boolean(uploadState);
    useAdminUnsavedChanges(!allowNavigationAfterSave && (isUploading || isSaving || hasUnsavedChanges));

    useEffect(() => {
        if (!allowNavigationAfterSave) return;

        const pendingNavigation = pendingSavedDraftNavigationRef.current;
        if (!pendingNavigation) return;
        pendingSavedDraftNavigationRef.current = null;

        if (
            editorOperationEpochRef.current !== pendingNavigation.editorEpoch
            || activeVideoIdRef.current !== pendingNavigation.videoId
        ) {
            setAllowNavigationAfterSave(false);
            return;
        }

        navigate(`${Routes.ADMIN_VIDEOS}/${pendingNavigation.videoId}`, {
            replace: true,
            state: { videosReturnTo: Routes.ADMIN_VIDEOS }
        });
    }, [allowNavigationAfterSave, navigate]);

    const hasActiveGeneration = Boolean(mediaHealth?.ActiveGeneration || mediaHealth?.ActiveMediaGenerationId);
    const activePlaybackFiles = mediaHealth?.ActiveGeneration?.Files
        ?? (hasActiveGeneration
            ? mediaHealth?.Files.filter((item) => (
                item.IsCurrentPlayback !== false
                && (!mediaHealth.ActiveMediaGenerationId || !item.MediaGenerationId || item.MediaGenerationId === mediaHealth.ActiveMediaGenerationId)
            )) ?? []
            : []);
    const activeMp4 = activePlaybackFiles.find((item) => item.Format === videoFormat.primaryPlayback);
    const activeWebm = activePlaybackFiles.find((item) => item.Format === videoFormat.webmPlayback);
    const configuredActiveMp4 = activeMp4?.FileStatus !== 5 ? activeMp4 : undefined;
    const configuredActiveWebm = activeWebm?.FileStatus !== 5 ? activeWebm : undefined;
    const readyActiveMp4 = configuredActiveMp4?.FileStatus === 2 ? configuredActiveMp4 : undefined;
    const readyActiveWebm = configuredActiveWebm?.FileStatus === 2 ? configuredActiveWebm : undefined;
    const activePlayback = (readyActiveMp4?.HasDatabaseRecord && readyActiveMp4.StorageObjectExists === true ? readyActiveMp4 : undefined)
        ?? (readyActiveWebm?.HasDatabaseRecord && readyActiveWebm.StorageObjectExists === true ? readyActiveWebm : undefined)
        ?? readyActiveMp4
        ?? readyActiveWebm
        ?? configuredActiveMp4
        ?? configuredActiveWebm;
    const playbackFileConfigured = mediaHealth?.CanPreview === false
        ? false
        : Boolean(activePlayback?.HasDatabaseRecord && activePlayback.FileStatus === 2);
    const playbackStorageStatus: 'complete' | 'incomplete' | 'unknown' = mediaErrorMessage
        ? 'unknown'
        : activePlayback?.StorageObjectExists === true
        ? 'complete'
        : activePlayback?.StorageObjectExists === false
            ? 'incomplete'
            : activePlayback
                ? 'unknown'
                : 'incomplete';
    const sourceFiles = [
        ...(mediaHealth?.CandidateGeneration?.Files ?? []),
        ...(mediaHealth?.ActiveGeneration?.Files ?? []),
        ...(mediaHealth?.Files ?? [])
    ].filter((item) => item.Format === videoFormat.originalSource);
    const sourceUploaded = sourceFiles.some((item) => item.HasDatabaseRecord && item.StorageObjectExists === true);
    type ChecklistStatus = 'complete' | 'incomplete' | 'unknown' | 'optional';
    const requiredChecklist: Array<{ label: string; status: ChecklistStatus }> = [
        { label: 'Title added', status: form.Title.trim().length > 0 ? 'complete' : 'incomplete' }
    ];
    const publicationChecklist: Array<{ label: string; status: ChecklistStatus }> = [
        {
            label: selectedVideoId ? 'Draft saved' : 'Draft not saved',
            status: selectedVideoId ? 'complete' : 'incomplete'
        },
        {
            label: mediaLoading ? 'Checking playable media' : 'Playable media ready',
            status: playbackFileConfigured ? 'complete' : 'incomplete'
        },
        {
            label: playbackStorageStatus === 'complete'
                ? 'Playback file found in storage'
                : playbackStorageStatus === 'unknown'
                    ? 'Playback storage check unavailable'
                    : activePlayback
                        ? 'Playback file missing from storage'
                        : 'Playable media not created yet',
            status: playbackStorageStatus
        }
    ];
    const optionalChecklist: Array<{ label: string; status: ChecklistStatus }> = [
        {
            label: sourceUploaded
                ? 'Source uploaded'
                : file
                    ? `Source selected: ${file.name}`
                    : 'Source can be added later',
            status: sourceUploaded || file ? 'complete' : 'optional'
        },
        { label: form.EventId ? 'Event selected' : 'Event not selected', status: form.EventId ? 'complete' : 'optional' },
        { label: `${form.Participants.filter((item) => item.ParticipantId && item.TypeId).length} participants assigned`, status: form.Participants.length > 0 ? 'complete' : 'optional' },
        { label: `${form.Tags.length} tags added`, status: form.Tags.length > 0 ? 'complete' : 'optional' }
    ];
    const isReadyToPublish = requiredChecklist.every((item) => item.status === 'complete')
        && publicationChecklist.every((item) => item.status === 'complete');
    const hasUnknownReadiness = publicationChecklist.some((item) => item.status === 'unknown');
    const canPreview = Boolean(
        detail?.Url?.trim()
        && mediaHealth?.CanPreview !== false
        && activePlayback?.HasDatabaseRecord
        && activePlayback.FileStatus === 2
        && activePlayback.StorageObjectExists === true
    );
    const editorTabs: Array<{ id: VideoEditorTab; label: string }> = [
        { id: 'details', label: 'Details' },
        { id: 'participants', label: 'Participants & tags' },
        { id: 'publishing', label: 'Publishing' },
        { id: 'media', label: 'Media' },
        ...(isSuperAdminUser ? [{ id: 'super-admin' as const, label: 'Super Admin' }] : [])
    ];
    const readinessPresentation = mediaLoading && !mediaHealth
            ? {
                title: 'Checking playback…',
                message: 'The video details are available while media is checked.',
                className: styles.readinessNeutral
            }
            : mediaErrorMessage
                ? {
                    title: 'Playback check unavailable',
                    message: 'This is not a failed readiness check. Retry the media check before publishing or previewing.',
                    className: styles.readinessWarning
                }
                : hasUnknownReadiness
                    ? {
                        title: 'Playback storage could not be verified',
                        message: 'The result is unknown, not failed. Retry the media check before publishing or previewing.',
                        className: styles.readinessWarning
                    }
                    : isReadyToPublish
                    ? {
                        title: 'Ready to publish',
                        message: 'Required details and playable media are available.',
                        className: styles.readinessReady
                    }
                    : form.PublishStatus === 1
                        ? {
                            title: 'Published — playback needs attention',
                            message: 'Viewers may not be able to play this video. Review the incomplete checks now.',
                            className: styles.readinessWarning
                        }
                        : {
                            title: 'Not ready to publish',
                            message: isCreateMode
                                ? 'Create the draft, upload its source, and wait for playable media before publishing.'
                                : 'Complete the checks shown before publishing.',
                            className: styles.readinessWarning
                        };

    const handleGenerateTitle = () => {
        const generatedTitle = generateAdminFightTitle(
            effectiveEventDate,
            form.EventOrder ?? null,
            form.Participants,
            participants,
            participantTypes
        );
        if (!generatedTitle) {
            const hasTwoParticipants = new Set(form.Participants.map((item) => item.ParticipantId).filter(Boolean)).size >= 2;
            const targetTab: VideoEditorTab = hasTwoParticipants ? 'details' : 'participants';
            const targetFieldId = hasTwoParticipants
                ? effectiveEventDate ? 'video-event-order' : 'video-date-override'
                : 'video-add-participant';
            setActiveTab(targetTab);
            window.requestAnimationFrame(() => document.getElementById(targetFieldId)?.focus());
            setSuccessMessage(null);
            setErrorMessage('To generate a title, add the fight date, fight number, and two participants. Fighter roles and red/blue corners improve ordering.');
            return;
        }

        setErrorMessage(null);
        setForm((previous) => ({ ...previous, Title: generatedTitle }));
        setSuccessMessage('Title generated from the fight details.');
    };

    if (routeVideoId && !detail) {
        return (
            <div className={styles.page}>
                <button className={styles.backButton} type="button" onClick={returnToVideos}>← Back to videos</button>
                {errorMessage ? (
                    <div className={styles.errorBanner} role="alert">
                        <span>{errorMessage}</span>
                        <button type="button" className={styles.secondary} onClick={() => {
                            setErrorMessage(null);
                            void loadDetail(routeVideoId);
                        }}>Try again</button>
                    </div>
                ) : <div className={styles.loadingState}>Loading video workspace…</div>}
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <header className={`${styles.header} ${styles.videoWorkspaceHeader}`}>
                <div>
                    {routeVideoId || isNewRoute ? <button className={styles.backButton} type="button" onClick={returnToVideos}>← Back to videos</button> : null}
                    <h1>{isCreateMode ? 'Create new video' : detail?.Title ?? 'Video'}</h1>
                    <p>{isCreateMode ? 'Add a title to save the draft. Selecting a source first is optional and starts its upload automatically.' : 'Work through each tab and save your changes.'}</p>
                </div>
                <div className={styles.actions}>
                    {detail ? <AdminVideoPlayerTools title={detail.Title} shortcode={detail.Url} canPreview={canPreview} token={token} /> : null}
                    {isCreateMode ? (
                        <button className={styles.primary} type="button" disabled={!canCreateDraft} onClick={handleCreateDraft}>
                            {isSaving ? 'Creating draft…' : 'Create draft'}
                        </button>
                    ) : (
                        <button className={styles.primary} type="button" disabled={isSaving || isUploading || !hasMetadataUnsavedChanges || !form.Title.trim()} onClick={handleSave}>
                            {isSaving ? 'Saving…' : metadataSaveRetryRequired ? 'Save details' : hasMetadataUnsavedChanges ? 'Save changes' : 'Saved'}
                        </button>
                    )}
                </div>
            </header>

            {errorMessage ? <div className={styles.errorBanner} role="alert">{errorMessage}</div> : null}
            {successMessage ? <div className={styles.muted} role="status">{successMessage}</div> : null}

            <div className={`${styles.content} ${styles.videoPageContent}`}>
                <section className={`${styles.panel} ${styles.videoWorkflowPanel}`}>
                    <h2>Video workflow</h2>
                    {detail ? (
                        <div className={styles.muted}>
                            Primary processing: {statusLabels[detail.Status] ?? detail.Status} | Duration: {formatDuration(detail.Duration)} | Effective Date: {formatDateTime(effectiveEventDate)}
                        </div>
                    ) : null}

                    <div className={styles.workflowNav} role="tablist" aria-label="Video editing sections">
                        {editorTabs.map((tab, index) => (
                            <button
                                key={tab.id}
                                id={`video-tab-${tab.id}`}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                aria-controls={`video-tabpanel-${tab.id}`}
                                tabIndex={activeTab === tab.id ? 0 : -1}
                                className={activeTab === tab.id ? styles.workflowTabActive : ''}
                                onClick={() => setActiveTab(tab.id)}
                                onKeyDown={(event) => {
                                    const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
                                    const targetIndex = event.key === 'Home'
                                        ? 0
                                        : event.key === 'End'
                                            ? editorTabs.length - 1
                                            : direction
                                                ? (index + direction + editorTabs.length) % editorTabs.length
                                                : -1;
                                    if (targetIndex < 0) return;
                                    event.preventDefault();
                                    const nextTab = editorTabs[targetIndex];
                                    setActiveTab(nextTab.id);
                                    window.requestAnimationFrame(() => document.getElementById(`video-tab-${nextTab.id}`)?.focus());
                                }}
                            >
                                {index + 1}. {tab.label}
                            </button>
                        ))}
                    </div>

                    {!isCreateMode && activeTab === 'media' ? (
                        <section id="video-tabpanel-media" role="tabpanel" aria-labelledby="video-tab-media" className={styles.mediaSection}>
                            <div className={styles.mediaHeader}>
                                <div>
                                    <h3>Media</h3>
                                    <p>
                                        Original source is the uploaded input. Primary and mobile playback are generated independently.
                                        Generate/retry creates a missing file record when needed and queues this existing video; it does not delete stored files.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className={styles.secondary}
                                    disabled={mediaLoading || !selectedVideoId}
                                    onClick={() => selectedVideoId && loadMediaHealth(selectedVideoId)}
                                >
                                    {mediaLoading ? 'Checking...' : 'Refresh media'}
                                </button>
                            </div>

                            <div className={styles.field}>
                                <label htmlFor="video-thumbnail-seconds">Thumbnail capture (seconds)</label>
                                <input
                                    id="video-thumbnail-seconds"
                                    type="number"
                                    min={0}
                                    step={1}
                                    placeholder="Defaults to 3 seconds"
                                    value={form.ThumbnailCaptureSeconds ?? ''}
                                    onChange={(event) => setForm({
                                        ...form,
                                        ThumbnailCaptureSeconds: event.target.value === '' ? null : Number(event.target.value)
                                    })}
                                />
                                <div className={styles.muted}>Used the next time a source is processed. Save changes before starting a new source upload.</div>
                            </div>

                            {(uploadState || mediaHealth?.AvailableActions?.includes('UploadSource') || (file && !mediaHealth?.CandidateGeneration)) ? (
                                <section className={styles.replacementPanel} aria-labelledby="upload-source-heading">
                                    <div>
                                        <strong id="upload-source-heading">Upload original source</strong>
                                        <p className={styles.muted}>Choose the MP4 input. The draft and its metadata stay safe if the upload needs to be resumed.</p>
                                    </div>
                                    <div className={styles.replacementControls}>
                                        <input
                                            id="video-file"
                                            aria-label="Source MP4 file"
                                            type="file"
                                            accept="video/mp4,.mp4"
                                            disabled={isUploading}
                                            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                                        />
                                        {file ? <span className={styles.muted}>{file.name} · {formatBytes(file.size)}</span> : null}
                                        {!uploadState && hasMetadataUnsavedChanges ? (
                                            <span className={styles.muted}>Save metadata changes before uploading the source.</span>
                                        ) : null}
                                        <button
                                            type="button"
                                            className={styles.primary}
                                            disabled={isUploading || !file || (!uploadState && hasMetadataUnsavedChanges)}
                                            onClick={uploadState ? handleRetryUpload : handleUploadSource}
                                        >
                                            {isUploading ? 'Uploading source…' : uploadState ? 'Resume source upload' : 'Upload source'}
                                        </button>
                                    </div>
                                </section>
                            ) : null}

                            {mediaLoading && !mediaHealth ? (
                                <div className={styles.muted}>Checking database records and storage objects...</div>
                            ) : mediaHealth ? (
                                <div className={styles.mediaGenerations}>
                                    {mediaHealth.ActiveGeneration || !mediaHealth.CandidateGeneration ? (
                                    <section className={styles.mediaGeneration}>
                                        <div className={styles.mediaGenerationHeader}>
                                            <div>
                                                <strong>Current playback</strong>
                                                <div className={styles.muted}>This generation continues serving viewers during replacement processing.</div>
                                            </div>
                                            {mediaHealth.ActiveGeneration ? (
                                                <div className={styles.generationIdentity}>
                                                    <span className={styles.status}>{generationLifecycleLabels[mediaHealth.ActiveGeneration.Lifecycle]}</span>
                                                    <span className={styles.mediaPath}>{mediaHealth.ActiveGeneration.Id}</span>
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className={styles.mediaGrid}>
                                    {(mediaHealth.ActiveGeneration?.Files ?? mediaHealth.Files).map((fileHealth) => {
                                        const isSource = fileHealth.Format === videoFormat.originalSource;
                                        const isPrimary = fileHealth.Format === videoFormat.primaryPlayback;
                                        const isMobile = fileHealth.Format === videoFormat.mobilePlayback;
                                        const isRepairable = isPrimary || isMobile;
                                        const isHealthy = fileHealth.FileStatus === 2 && fileHealth.StorageObjectExists === true;
                                        const isProcessing = fileHealth.FileStatus === 1;
                                        const repairBlockedByParent = (isMobile && mediaHealth.PrimaryProcessingStatus !== 2)
                                            || (isPrimary && mediaHealth.PrimaryProcessingStatus === 4);
                                        const actionLabel = !fileHealth.HasDatabaseRecord
                                            ? `Generate ${isMobile ? 'mobile' : 'primary'}`
                                            : `Generate / retry ${isMobile ? 'mobile' : 'primary'}`;
                                        const sourceUploadStatus = !fileHealth.HasDatabaseRecord
                                            ? 'Missing'
                                            : fileHealth.StorageObjectExists === true
                                                ? 'Uploaded'
                                                : fileHealth.StorageObjectExists === false
                                                    ? 'Missing'
                                                    : 'Not checked';
                                        const displayStatus = isSource
                                            ? sourceUploadStatus
                                            : fileHealth.FileStatus == null
                                                ? 'Not available'
                                                : statusLabels[fileHealth.FileStatus] ?? fileHealth.FileStatus;
                                        const storageStatus = fileHealth.StorageObjectExists === true
                                            ? 'Found'
                                            : fileHealth.StorageObjectExists === false
                                                ? 'Missing'
                                                : 'Not checked';

                                        return (
                                            <article className={styles.mediaCard} key={fileHealth.Format}>
                                                <div className={styles.mediaCardTitle}>
                                                    <strong>{fileHealth.RoleLabel}</strong>
                                                    <span className={styles.status}>{displayStatus}</span>
                                                </div>
                                                <dl className={styles.mediaFacts}>
                                                    {isSource ? (
                                                        <>
                                                            <div><dt>Original file</dt><dd>{fileHealth.OriginalFilename || 'Not retained for this legacy upload'}</dd></div>
                                                            <div><dt>Upload status</dt><dd>{sourceUploadStatus}</dd></div>
                                                            <div><dt>Size</dt><dd>{formatBytes(fileHealth.StorageFileSize ?? fileHealth.DatabaseFileSize)}</dd></div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div><dt>DB record</dt><dd>{fileHealth.HasDatabaseRecord ? 'Found' : 'Missing'}</dd></div>
                                                            <div><dt>Storage</dt><dd>{storageStatus}</dd></div>
                                                            <div><dt>Processing status</dt><dd>{fileHealth.FileStatus == null ? 'Not available' : statusLabels[fileHealth.FileStatus] ?? fileHealth.FileStatus}</dd></div>
                                                            <div><dt>Size</dt><dd>{formatBytes(fileHealth.StorageFileSize ?? fileHealth.DatabaseFileSize)}</dd></div>
                                                        </>
                                                    )}
                                                </dl>
                                                {isSuperAdminUser && fileHealth.Path ? (
                                                    <details className={styles.technicalDetails}>
                                                        <summary>Technical storage details</summary>
                                                        <div className={styles.mediaPath}>{fileHealth.Path}</div>
                                                        <div className={styles.muted}>Database status: {fileHealth.FileStatus == null ? 'Not available' : statusLabels[fileHealth.FileStatus] ?? fileHealth.FileStatus}</div>
                                                    </details>
                                                ) : null}
                                                {!isHealthy && fileHealth.LatestFailure ? (
                                                    <div className={styles.mediaFailure}>
                                                        <strong>{fileHealth.LatestFailure.Category}: {fileHealth.LatestFailure.Code}</strong>
                                                        <span>{fileHealth.LatestFailure.Message}</span>
                                                        <span>
                                                            {fileHealth.LatestFailure.Retryable
                                                                ? 'Retryable failure — queue a retry after checking storage connectivity.'
                                                                : 'Not marked retryable — review the source or transcode settings before retrying.'}
                                                        </span>
                                                    </div>
                                                ) : null}
                                                {isRepairable && !isHealthy ? (
                                                    <button
                                                        type="button"
                                                        className={styles.secondary}
                                                        disabled={repairingFormat != null || mediaAction != null || isProcessing || repairBlockedByParent}
                                                        onClick={() => handleRepairMedia(mediaHealth.VideoId, fileHealth)}
                                                    >
                                                        {repairingFormat === fileHealth.Format ? 'Queuing...' : actionLabel}
                                                    </button>
                                                ) : null}
                                                {isRepairable && repairBlockedByParent ? (
                                                    <div className={styles.muted}>
                                                        {isMobile
                                                            ? 'Mobile processing can be queued after primary processing is Ready.'
                                                            : 'Primary processing is already claimed by a worker.'}
                                                    </div>
                                                ) : null}
                                            </article>
                                        );
                                    })}
                                        </div>
                                    </section>
                                    ) : null}

                                    {!mediaHealth.CandidateGeneration && mediaHealth.AvailableActions?.includes('ReplaceSource') ? (
                                        <section className={styles.replacementPanel}>
                                            <div>
                                                <strong>Replace source video</strong>
                                                <p className={styles.replacementWarning}>
                                                    The existing video, URL, and metadata are retained. Current playback stays live and switches only after primary processing succeeds. Existing and failed replacement files are retained; mobile processing happens separately.
                                                </p>
                                            </div>
                                            <div className={styles.replacementControls}>
                                                <input
                                                    aria-label="Replacement MP4 file"
                                                    type="file"
                                                    accept="video/mp4,.mp4"
                                                    disabled={mediaAction != null || hasMetadataUnsavedChanges}
                                                    onChange={(event) => setReplacementFile(event.target.files?.[0] ?? null)}
                                                />
                                                {hasMetadataUnsavedChanges ? (
                                                    <span className={styles.muted}>Save metadata changes before replacing the source.</span>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    className={styles.danger}
                                                    disabled={mediaAction != null || !replacementFile || hasMetadataUnsavedChanges}
                                                    onClick={() => handleReplacementUpload()}
                                                >
                                                    {mediaAction === 'replace-source' ? 'Uploading replacement...' : 'Replace source video'}
                                                </button>
                                            </div>
                                        </section>
                                    ) : null}

                                    {mediaHealth.CandidateGeneration ? (
                                        <section className={`${styles.mediaGeneration} ${styles.candidateGeneration}`}>
                                            <div className={styles.mediaGenerationHeader}>
                                                <div>
                                                    <strong>{mediaHealth.ActiveGeneration ? 'Candidate replacement' : 'Initial upload'}</strong>
                                                    <div className={styles.replacementWarning}>
                                                        {mediaHealth.ActiveGeneration
                                                            ? 'Not live. Current playback remains unchanged until candidate primary verification succeeds.'
                                                            : 'Not live yet. Playback becomes available after primary processing succeeds.'}
                                                    </div>
                                                </div>
                                                <div className={styles.generationIdentity}>
                                                    <span className={styles.status}>{generationLifecycleLabels[mediaHealth.CandidateGeneration.Lifecycle]}</span>
                                                    <span className={styles.mediaPath}>{mediaHealth.CandidateGeneration.Id}</span>
                                                </div>
                                            </div>
                                            <div className={styles.mediaGrid}>
                                                {mediaHealth.CandidateGeneration.Files
                                                    .filter((fileHealth) => fileHealth.Format !== videoFormat.mobilePlayback)
                                                    .map((fileHealth) => {
                                                        const isSource = fileHealth.Format === videoFormat.originalSource;
                                                        const isHealthy = fileHealth.FileStatus === 2 && fileHealth.StorageObjectExists === true;
                                                        const sourceUploadStatus = !fileHealth.HasDatabaseRecord
                                                            ? 'Missing'
                                                            : fileHealth.StorageObjectExists === true
                                                                ? 'Uploaded'
                                                                : fileHealth.StorageObjectExists === false
                                                                    ? 'Missing'
                                                                    : 'Not checked';
                                                        const displayStatus = isSource
                                                            ? sourceUploadStatus
                                                            : fileHealth.FileStatus == null
                                                                ? 'Not available'
                                                                : statusLabels[fileHealth.FileStatus] ?? fileHealth.FileStatus;
                                                        const storageStatus = fileHealth.StorageObjectExists === true
                                                            ? 'Found'
                                                            : fileHealth.StorageObjectExists === false
                                                                ? 'Missing'
                                                                : 'Not checked';

                                                        return (
                                                            <article className={styles.mediaCard} key={`${mediaHealth.CandidateGeneration?.Id}-${fileHealth.Format}`}>
                                                                <div className={styles.mediaCardTitle}>
                                                                    <strong>{fileHealth.RoleLabel}</strong>
                                                                    <span className={styles.status}>{displayStatus}</span>
                                                                </div>
                                                                <dl className={styles.mediaFacts}>
                                                                    {isSource ? (
                                                                        <>
                                                                            <div><dt>Original file</dt><dd>{fileHealth.OriginalFilename || 'Not retained for this legacy upload'}</dd></div>
                                                                            <div><dt>Upload status</dt><dd>{sourceUploadStatus}</dd></div>
                                                                            <div><dt>Size</dt><dd>{formatBytes(fileHealth.StorageFileSize ?? fileHealth.DatabaseFileSize)}</dd></div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div><dt>DB record</dt><dd>{fileHealth.HasDatabaseRecord ? 'Found' : 'Missing'}</dd></div>
                                                                            <div><dt>Storage</dt><dd>{storageStatus}</dd></div>
                                                                            <div><dt>Processing status</dt><dd>{fileHealth.FileStatus == null ? 'Not available' : statusLabels[fileHealth.FileStatus] ?? fileHealth.FileStatus}</dd></div>
                                                                            <div><dt>Size</dt><dd>{formatBytes(fileHealth.StorageFileSize ?? fileHealth.DatabaseFileSize)}</dd></div>
                                                                        </>
                                                                    )}
                                                                </dl>
                                                                {isSuperAdminUser && fileHealth.Path ? (
                                                                    <details className={styles.technicalDetails}>
                                                                        <summary>Technical storage details</summary>
                                                                        <div className={styles.mediaPath}>{fileHealth.Path}</div>
                                                                        <div className={styles.muted}>Database status: {fileHealth.FileStatus == null ? 'Not available' : statusLabels[fileHealth.FileStatus] ?? fileHealth.FileStatus}</div>
                                                                    </details>
                                                                ) : null}
                                                                {!isHealthy && fileHealth.LatestFailure ? (
                                                                    <div className={styles.mediaFailure}>
                                                                        <strong>{fileHealth.LatestFailure.Category}: {fileHealth.LatestFailure.Code}</strong>
                                                                        <span>{fileHealth.LatestFailure.Message}</span>
                                                                    </div>
                                                                ) : null}
                                                            </article>
                                                        );
                                                    })}
                                            </div>
                                            <div className={styles.replacementControls}>
                                                {mediaHealth.CandidateGeneration.AvailableActions.includes('RetryUpload') ? (
                                                    <div className={styles.replacementUploadControls}>
                                                        <input
                                                            aria-label="Candidate replacement MP4 file"
                                                            type="file"
                                                            accept="video/mp4,.mp4"
                                                            disabled={mediaAction != null}
                                                            onChange={(event) => setReplacementFile(event.target.files?.[0] ?? null)}
                                                        />
                                                        <button
                                                            type="button"
                                                            className={styles.secondary}
                                                            disabled={mediaAction != null || !replacementFile}
                                                            onClick={() => handleReplacementUpload(mediaHealth.CandidateGeneration?.Id)}
                                                        >
                                                            {mediaAction === 'retry-upload' ? 'Uploading...' : 'Retry upload'}
                                                        </button>
                                                    </div>
                                                ) : null}
                                                {mediaHealth.CandidateGeneration.AvailableActions.includes('RetryPrimary') ? (
                                                    <button
                                                        type="button"
                                                        className={styles.secondary}
                                                        disabled={mediaAction != null}
                                                        onClick={() => handleRetryReplacementPrimary(mediaHealth.CandidateGeneration!.Id)}
                                                    >
                                                        {mediaAction === 'retry-primary' ? 'Queuing...' : 'Retry primary processing'}
                                                    </button>
                                                ) : null}
                                                {mediaHealth.ActiveGeneration && mediaHealth.CandidateGeneration.AvailableActions.includes('AbandonReplacement') ? (
                                                    <button
                                                        type="button"
                                                        className={styles.danger}
                                                        disabled={mediaAction != null}
                                                        onClick={() => handleAbandonReplacement(mediaHealth.CandidateGeneration!.Id)}
                                                    >
                                                        {mediaAction === 'abandon' ? 'Abandoning...' : 'Abandon replacement'}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </section>
                                    ) : null}
                                </div>
                            ) : mediaErrorMessage ? (
                                <div className={styles.mediaCheckWarning} role="status">
                                    <strong>Playback check unavailable</strong>
                                    <span>{mediaErrorMessage}</span>
                                    <button type="button" className={styles.secondary} disabled={mediaLoading || !selectedVideoId} onClick={() => selectedVideoId && loadMediaHealth(selectedVideoId, true)}>
                                        Try again
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.muted}>Playback has not been checked yet.</div>
                            )}
                        </section>
                    ) : null}

                    {isCreateMode && activeTab === 'media' ? (
                        <section id="video-tabpanel-media" role="tabpanel" aria-labelledby="video-tab-media" className={styles.workflowSection}>
                            <div className={styles.sectionHeading}>
                                <h3>Media</h3>
                                <p>The source is optional while creating the draft. If selected now, its upload starts automatically after the draft is saved.</p>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="video-file">Video File</label>
                                <input
                                    id="video-file"
                                    type="file"
                                    accept="video/mp4,.mp4"
                                    disabled={isSaving || isUploading}
                                    onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                                />
                                {file ? <div className={styles.muted}>{file.name} · {formatBytes(file.size)}</div> : null}
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="video-thumbnail-seconds">Thumbnail capture (seconds)</label>
                                <input
                                    id="video-thumbnail-seconds"
                                    type="number"
                                    min={0}
                                    step={1}
                                    placeholder="Defaults to 3 seconds"
                                    value={form.ThumbnailCaptureSeconds ?? ''}
                                    onChange={(event) => setForm({
                                        ...form,
                                        ThumbnailCaptureSeconds: event.target.value === '' ? null : Number(event.target.value)
                                    })}
                                />
                            </div>
                        </section>
                    ) : null}

                    {activeTab === 'details' || activeTab === 'publishing' ? (
                    <div
                        id={`video-tabpanel-${activeTab}`}
                        role="tabpanel"
                        aria-labelledby={`video-tab-${activeTab}`}
                        className={styles.workflowTabPanel}
                    >
                    {activeTab === 'details' ? (
                    <section className={`${styles.workflowSection} ${styles.workflowDetails}`}>
                        <div className={styles.sectionHeading}>
                            <h3>1. Details</h3>
                            <p>Use clear public-facing information so viewers and other administrators can identify this video.</p>
                        </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabelRow}>
                            <label htmlFor="video-title">Title</label>
                            <button type="button" className={styles.textAction} onClick={handleGenerateTitle}>Generate from fight details</button>
                        </div>
                        <input
                            id="video-title"
                            value={form.Title}
                            onChange={(event) => setForm({ ...form, Title: event.target.value })}
                            maxLength={200}
                        />
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="video-description">Description</label>
                        <textarea
                            id="video-description"
                            value={form.Description ?? ''}
                            onChange={(event) => setForm({ ...form, Description: event.target.value })}
                            maxLength={2000}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Video Shortcode</label>
                        <div className={styles.inlineValueRow}>
                            <div id="video-shortcode" className={styles.inlineValue}>
                                {detail?.Url ?? 'Not assigned yet'}
                            </div>
                            <button
                                type="button"
                                className={styles.secondary}
                                onClick={handleCopyShortcode}
                                disabled={!detail?.Url}
                            >
                                Copy
                            </button>
                        </div>
                        <div className={styles.muted}>
                            {detail?.Url
                                ? `Use this shortcode for highlights or watch links (for example: /watch?v=${detail.Url}).`
                                : 'Shortcode is assigned after the draft is created.'}
                        </div>
                    </div>

                    </section>
                    ) : null}

                    {activeTab === 'publishing' ? (
                    <section className={`${styles.workflowSection} ${styles.workflowPublishing}`}>
                        <div className={styles.sectionHeading}>
                            <h3>3. Publishing and access</h3>
                            <p>Choose who can see the video. Publishing never repairs missing media automatically.</p>
                        </div>

                    <div className={styles.field}>
                        <label htmlFor="video-publish-status">Publish Status</label>
                        <select
                            id="video-publish-status"
                            value={String(form.PublishStatus)}
                            onChange={(event) => {
                                const publishStatus = Number(event.target.value);
                                if (publishStatus === 1 && detail?.PublishStatus !== 1 && !isReadyToPublish) {
                                    setErrorMessage('Publishing is available after playable media is Ready and verified in storage.');
                                    setSuccessMessage(null);
                                    return;
                                }
                                setErrorMessage(null);
                                setForm({ ...form, PublishStatus: publishStatus });
                            }}
                        >
                            <option value="0">Not published</option>
                            <option value="1" disabled={!isReadyToPublish && detail?.PublishStatus !== 1}>Published</option>
                        </select>
                        {!isReadyToPublish && detail?.PublishStatus !== 1 ? <div className={styles.muted}>Upload and process playable media before publishing.</div> : null}
                    </div>

                    </section>
                    ) : null}

                    {activeTab === 'details' ? (
                    <section className={`${styles.workflowSection} ${styles.workflowDetails}`}>
                        <div className={styles.sectionHeading}>
                            <h3>Event information</h3>
                            <p>Selecting an event automatically supplies its venue and date unless an override is entered.</p>
                        </div>

                    <div className={styles.field}>
                        <label htmlFor="video-event-id">Event</label>
                        <SearchDropdown
                            id="video-event-id"
                            value={form.EventId ?? ''}
                            options={activeEventOptions}
                            onChange={(value) => {
                                setForm({
                                    ...form,
                                    EventId: value || null,
                                    LocationId: value ? null : form.LocationId
                                });
                                const selectedEventOption = activeEventOptions.find((option) => option.value === (value ?? ''));
                                setEventSearchInput(selectedEventOption?.label ?? '');
                            }}
                            onSearchChange={setEventSearchInput}
                            placeholder="Search event"
                            noResultsText="No events found."
                        />
                        <div className={styles.actions}>
                            <button type="button" className={styles.secondary} onClick={handleOpenCreateEventModal}>
                                Create Event
                            </button>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="video-location-id">Venue</label>
                        <SearchDropdown
                            id="video-location-id"
                            value={form.LocationId ?? ''}
                            options={activeLocationOptions}
                            onChange={(value) => {
                                const nextValue = value || null;
                                setForm({ ...form, LocationId: nextValue });
                                const selectedLocation = activeLocationOptions.find((option) => option.value === (nextValue ?? ''));
                                setLocationSearchInput(selectedLocation?.label ?? '');
                            }}
                            onSearchChange={setLocationSearchInput}
                            placeholder="Search venue"
                            noResultsText="No venues found."
                            disabled={!canEditLocation}
                        />
                        {canEditLocation ? (
                            <div className={styles.actions}>
                                <button type="button" className={styles.secondary} onClick={() => handleOpenCreateVenueModal('video-form')}>
                                    Create Venue
                                </button>
                            </div>
                        ) : null}
                        {selectedEvent ? (
                            <div className={styles.muted}>
                                Venue is derived from event: {selectedEvent.locationName ?? 'No event venue'}
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="video-event-order">Fight number (event order)</label>
                        <input
                            id="video-event-order"
                            type="number"
                            min={1}
                            step={1}
                            value={form.EventOrder ?? ''}
                            onChange={(event) => {
                                const value = event.target.value.trim();
                                if (!value) {
                                    setForm({ ...form, EventOrder: null });
                                    return;
                                }

                                const parsed = Number.parseInt(value, 10);
                                setForm({ ...form, EventOrder: Number.isNaN(parsed) ? null : parsed });
                            }}
                        />
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="video-date-override">Fight date/time override</label>
                        <AdminDateTimeInput
                            id="video-date-override"
                            value={form.VideoDateOverride ?? ''}
                            includeTime
                            onChange={(value) => setForm({ ...form, VideoDateOverride: value || null })}
                        />
                        <div className={styles.muted}>
                            The selected event date is used by default. Set this only when the fight date/time differs. Effective date: {formatDateTime(effectiveEventDate)}
                        </div>
                    </div>

                    </section>
                    ) : null}

                    {activeTab === 'publishing' ? (
                    <section className={`${styles.workflowSection} ${styles.workflowPublishing}`}>

                    <div className={styles.field}>
                        <label htmlFor="video-purchase-enabled">Purchases Enabled</label>
                        <input
                            id="video-purchase-enabled"
                            type="checkbox"
                            checked={form.PurchaseEnabled ?? true}
                            onChange={(event) => setForm({ ...form, PurchaseEnabled: event.target.checked })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="video-purchase-price">Purchase Price Override (NZD)</label>
                        <input
                            id="video-purchase-price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.PurchasePrice ?? ''}
                            onChange={(event) => {
                                const value = event.target.value.trim();
                                setForm({ ...form, PurchasePrice: value === '' ? null : Number(value) });
                            }}
                        />
                        <div className={styles.muted}>
                            Leave blank to use the default price. Use 0 for free access.
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="video-purchase-access-until">Purchase Access Until</label>
                        <AdminDateTimeInput
                            id="video-purchase-access-until"
                            value={form.PurchaseAccessUntil ?? ''}
                            onChange={(value) => setForm({ ...form, PurchaseAccessUntil: value || null })}
                        />
                        <div className={styles.muted}>
                            Leave blank to use the default access date.
                        </div>
                    </div>

                    </section>
                    ) : null}

                    </div>
                    ) : null}

                    {isSuperAdminUser && activeTab === 'super-admin' ? (
                        <section id="video-tabpanel-super-admin" role="tabpanel" aria-labelledby="video-tab-super-admin" className={styles.workflowSection}>
                            <div className={styles.sectionHeading}>
                                <h3>Super Admin controls</h3>
                                <p>These advanced overrides affect this video only and are visible only to Super Admins.</p>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="video-mobile-playback-override">Mobile playback</label>
                                <select
                                    id="video-mobile-playback-override"
                                    value={mobilePlaybackOverride === null ? 'global' : mobilePlaybackOverride ? 'enabled' : 'disabled'}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setMobilePlaybackOverride(value === 'global' ? null : value === 'enabled');
                                    }}
                                >
                                    <option value="global">Use global setting</option>
                                    <option value="enabled">Force mobile playback on</option>
                                    <option value="disabled">Force mobile playback off</option>
                                </select>
                                <div className={styles.muted}>Use this only for rollout testing or to bypass a bad mobile transcode.</div>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="video-presigned-delivery-override">Video delivery</label>
                                <select
                                    id="video-presigned-delivery-override"
                                    value={presignedDeliveryOverride === null ? 'global' : presignedDeliveryOverride ? 'presigned' : 'api'}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setPresignedDeliveryOverride(value === 'global' ? null : value === 'presigned');
                                    }}
                                >
                                    <option value="global">Use global setting</option>
                                    <option value="presigned">Force pre-signed URL</option>
                                    <option value="api" disabled={!apiStreamFallbackEnabled}>Force API stream</option>
                                </select>
                                <div className={styles.muted}>
                                    Use this to test direct storage delivery on one video before changing the site-wide setting.
                                    {!apiStreamFallbackEnabled ? ' Force API stream requires the global API fallback to be enabled first.' : ''}
                                </div>
                            </div>
                        </section>
                    ) : null}

                    {activeTab === 'participants' ? (
                    <section id="video-tabpanel-participants" role="tabpanel" aria-labelledby="video-tab-participants" className={`${styles.workflowSection} ${styles.workflowParticipants}`}>
                        <div className={styles.sectionHeading}>
                            <h3>2. Participants and tags</h3>
                            <p>Add the people and search terms viewers will use to find this video.</p>
                        </div>

                    <div className={styles.field}>
                        <label>Participants</label>
                        {form.Participants.map((assignment, index) => (
                            <div className={styles.actions} key={`participant-${index}`}>
                                <SearchDropdown
                                    value={assignment.ParticipantId}
                                    id={`video-participant-${index}`}
                                    options={participantOptions}
                                    onChange={(value) => {
                                        const nextValue = value ?? '';
                                        updateParticipantAssignment(index, 'ParticipantId', nextValue);
                                        const selected = participantOptions.find((option) => option.value === nextValue);
                                        setParticipantSearchByIndex((previous) => ({
                                            ...previous,
                                            [index]: selected?.label ?? ''
                                        }));
                                    }}
                                    onSearchChange={(query) => {
                                        setParticipantSearchByIndex((previous) => ({
                                            ...previous,
                                            [index]: query
                                        }));
                                    }}
                                    placeholder="Search participant"
                                    noResultsText="No participants found."
                                />
                                <SearchDropdown
                                    value={assignment.TypeId}
                                    id={`video-participant-role-${index}`}
                                    options={participantTypeOptions}
                                    onChange={(value) => updateParticipantAssignment(index, 'TypeId', value ?? '')}
                                    placeholder="Search role"
                                    noResultsText="No roles found."
                                />
                                <SearchDropdown
                                    value={assignment.CornerColor ?? ''}
                                    id={`video-participant-corner-${index}`}
                                    options={cornerColorOptions.map((cornerColor) => ({
                                        value: cornerColor.value,
                                        label: cornerColor.label
                                    }))}
                                    onChange={(value) => updateParticipantAssignment(index, 'CornerColor', value ?? '')}
                                    placeholder="Search corner"
                                    noResultsText="No corner options."
                                />
                                <button type="button" className={styles.danger} onClick={() => removeParticipantAssignment(index)}>
                                    Remove
                                </button>
                                <button
                                    type="button"
                                    className={styles.secondary}
                                    onClick={() => handleOpenCreateParticipantModal(index)}
                                >
                                    Create Participant
                                </button>
                            </div>
                        ))}
                        <button id="video-add-participant" type="button" className={styles.secondary} onClick={addParticipantAssignment}>
                            Add participant
                        </button>
                    </div>

                    <div className={styles.field}>
                        <label>Tags</label>
                        <div className={styles.actions}>
                            <input
                                value={tagInput}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setTagInput(value);
                                    addExistingTagByName(value);
                                }}
                                list="existing-tag-list"
                                placeholder="Search existing tags"
                                maxLength={200}
                            />
                            <datalist id="existing-tag-list">
                                {availableTags.map((tag) => (
                                    <option key={tag.id} value={tag.name} />
                                ))}
                            </datalist>
                            <button type="button" className={styles.secondary} onClick={createTag}>
                                Create new
                            </button>
                        </div>
                        <div className={styles.actions}>
                            {form.Tags.map((tag) => (
                                <button key={tag} type="button" className={styles.secondary} onClick={() => removeTag(tag)}>
                                    #{tag} x
                                </button>
                            ))}
                        </div>
                        {eventTagLink && eventTagSummary.eventTags.length > 0 ? (
                            <div className={styles.tagSummary}>
                                <span className={styles.muted}>Event tags ({eventTagSummary.eventTags.length})</span>
                                <div className={styles.tagRow}>
                                    {eventTagSummary.preview.map((tag) => (
                                        <Link key={tag} className={styles.tagLink} to={eventTagLink}>
                                            #{tag}
                                        </Link>
                                    ))}
                                    {eventTagSummary.remaining > 0 ? (
                                        <Link className={styles.tagLink} to={eventTagLink}>
                                            +{eventTagSummary.remaining} more...
                                        </Link>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    </section>
                    ) : null}

                </section>
                <aside className={`${styles.workflowChecklist} ${readinessPresentation.className}`} aria-labelledby="video-checklist-heading">
                    <div>
                        <h2 id="video-checklist-heading">{readinessPresentation.title}</h2>
                        <p>{readinessPresentation.message}</p>
                    </div>
                    <section>
                        <h3>Required to save</h3>
                        <ul>
                            {requiredChecklist.map((item) => (
                                <li key={item.label} className={item.status === 'complete' ? styles.checkComplete : styles.checkIncomplete}>
                                    <span aria-hidden="true">{item.status === 'complete' ? '✓' : '○'}</span>{item.label}
                                </li>
                            ))}
                        </ul>
                    </section>
                    <section>
                        <h3>Before publishing</h3>
                        <ul>
                            {publicationChecklist.map((item) => (
                                <li key={item.label} className={item.status === 'complete' ? styles.checkComplete : item.status === 'unknown' ? styles.checkUnknown : styles.checkIncomplete}>
                                    <span aria-hidden="true">{item.status === 'complete' ? '✓' : item.status === 'unknown' ? '?' : '○'}</span>{item.label}
                                </li>
                            ))}
                        </ul>
                    </section>
                    <section>
                        <h3>Optional details</h3>
                        <ul>
                            {optionalChecklist.map((item) => (
                                <li key={item.label} className={item.status === 'complete' ? styles.checkComplete : styles.checkOptional}>
                                    <span aria-hidden="true">{item.status === 'complete' ? '✓' : '·'}</span>{item.label}
                                </li>
                            ))}
                        </ul>
                    </section>
                    {file ? (
                        <section className={styles.selectedSourceSummary} aria-label="Selected source file">
                            <h3>Selected source</h3>
                            <strong>{file.name}</strong>
                            <span>{formatBytes(file.size)}</span>
                            <div className={styles.actions}>
                                <label className={styles.secondary}>
                                    Change
                                    <input
                                        className={styles.sourceChangeInput}
                                        aria-label="Change selected source file"
                                        type="file"
                                        accept="video/mp4,.mp4"
                                        disabled={isUploading}
                                        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                                    />
                                </label>
                                <button type="button" className={styles.secondary} disabled={isUploading} onClick={() => setFile(null)}>Remove</button>
                            </div>
                        </section>
                    ) : null}
                    {!isCreateMode && (mediaErrorMessage || hasUnknownReadiness) && selectedVideoId ? (
                        <button type="button" className={styles.secondary} disabled={mediaLoading} onClick={() => loadMediaHealth(selectedVideoId, true)}>
                            Retry playback check
                        </button>
                    ) : null}
                </aside>
            </div>
            {isCreateEventModalOpen ? (
                <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="video-create-event-title">
                    <button
                        type="button"
                        className={styles.modalBackdrop}
                        aria-label="Close Create Event modal"
                        disabled={isCreatingEvent}
                        onClick={handleCloseCreateEventModal}
                    />
                    <div className={styles.modalPanel}>
                        <div className={styles.header}>
                            <div>
                                <h2 id="video-create-event-title">Create Event</h2>
                                <p>Add a new event without leaving the video editor.</p>
                            </div>
                        </div>
                        <form onSubmit={handleCreateEventFromModal}>
                            {createEventError ? <div className={styles.muted}>{createEventError}</div> : null}
                            <fieldset className={styles.modalFormFields} disabled={isCreatingEvent}>
                                <EventFormFields
                                    form={createEventForm}
                                    onChange={setCreateEventForm}
                                    fieldClassName={styles.field}
                                    actionsClassName={styles.actions}
                                    secondaryButtonClassName={styles.secondary}
                                    locationOptions={activeLocationOptions}
                                    onLocationSearchChange={setCreateEventLocationSearchInput}
                                    onCreateVenueClick={() => handleOpenCreateVenueModal('event-modal')}
                                    showCreateVenueButton
                                    idPrefix="video-create-event"
                                />
                            </fieldset>
                            <div className={styles.actions}>
                                <button type="submit" className={styles.primary} disabled={isCreatingEvent}>
                                    {isCreatingEvent ? 'Creating...' : 'Create and Select'}
                                </button>
                                <button type="button" className={styles.secondary} onClick={handleCloseCreateEventModal} disabled={isCreatingEvent}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
            {isCreateParticipantModalOpen ? (
                <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="video-create-participant-title">
                    <button
                        type="button"
                        className={styles.modalBackdrop}
                        aria-label="Close Create Participant modal"
                        disabled={isCreatingParticipant}
                        onClick={handleCloseCreateParticipantModal}
                    />
                    <div className={styles.modalPanel}>
                        <div className={styles.header}>
                            <div>
                                <h2 id="video-create-participant-title">Create Participant</h2>
                                <p>Add a new participant without leaving the video editor.</p>
                            </div>
                        </div>
                        <form onSubmit={handleCreateParticipantFromModal}>
                            {createParticipantError ? <div className={styles.muted}>{createParticipantError}</div> : null}
                            <fieldset className={styles.modalFormFields} disabled={isCreatingParticipant}>
                                <ParticipantFormFields
                                    form={createParticipantForm}
                                    onChange={setCreateParticipantForm}
                                    fieldClassName={styles.field}
                                    idPrefix="video-create-participant"
                                />
                            </fieldset>
                            <div className={styles.actions}>
                                <button type="submit" className={styles.primary} disabled={isCreatingParticipant}>
                                    {isCreatingParticipant ? 'Creating...' : 'Create and Select'}
                                </button>
                                <button type="button" className={styles.secondary} onClick={handleCloseCreateParticipantModal} disabled={isCreatingParticipant}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
            {isCreateVenueModalOpen ? (
                <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="video-create-venue-title">
                    <button
                        type="button"
                        className={styles.modalBackdrop}
                        aria-label="Close Create Venue modal"
                        disabled={isCreatingVenue}
                        onClick={handleCloseCreateVenueModal}
                    />
                    <div className={styles.modalPanel}>
                        <div className={styles.header}>
                            <div>
                                <h2 id="video-create-venue-title">Create Venue</h2>
                                <p>Add a venue without leaving the video editor.</p>
                            </div>
                        </div>
                        <form onSubmit={handleCreateVenueFromModal}>
                            {createVenueError ? <div className={styles.muted}>{createVenueError}</div> : null}
                            <fieldset className={styles.modalFormFields} disabled={isCreatingVenue}>
                                <VenueFormFields
                                    form={createVenueForm}
                                    onChange={setCreateVenueForm}
                                    fieldClassName={styles.field}
                                    idPrefix="video-create-venue"
                                />
                            </fieldset>
                            <div className={styles.actions}>
                                <button type="submit" className={styles.primary} disabled={isCreatingVenue}>
                                    {isCreatingVenue ? 'Creating...' : 'Create and Select'}
                                </button>
                                <button type="button" className={styles.secondary} onClick={handleCloseCreateVenueModal} disabled={isCreatingVenue}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default AdminVideos;
